import ArthurDreamReview, { DreamReviewInsight } from './ArthurDreamReview';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

// Same-origin API base, keyed on PAGE PROTOCOL (the pattern set in App.tsx — the
// canonical rule every page, incl. future domain pages, inherits). NOT NODE_ENV: the
// CRA dev server is `development` even behind an HTTPS front (Tailscale Serve), so
// keying on env appended :5000 under HTTPS and pointed at nothing.
//   • HTTPS page → same-origin :443, no port (herexy nginx / Tailscale Serve path-mount).
//   • HTTP page  → Flask :5000 direct — the countersigned LAN/Electron path, unchanged.
const API_BASE = window.location.protocol === 'https:'
  ? `https://${window.location.host}/api`
  : `${window.location.protocol}//${window.location.hostname}:5000/api`;

// ── COLOUR TOKENS (matches CodePage / Rexy dark theme) ──────────────────────
const C = {
  bg:         '#0f0f12',
  card:       '#16161a',
  cardBorder: 'rgba(255,255,255,0.07)',
  cardBorder2:'rgba(255,255,255,0.04)',
  text:       '#e8e8ec',
  textSub:    '#9090a0',
  textMuted:  '#55556a',
  accent:     '#7F77DD',   // purple — Sparticus / primary
  green:      '#1D9E75',
  greenLight: '#E1F5EE',
  greenDark:  '#085041',
  red:        '#E05252',
  redLight:   '#FCEBEB',
  redDark:    '#791F1F',
  amber:      '#BA7517',
  amberLight: '#FAEEDA',
  border:     'rgba(255,255,255,0.08)',
  chart1:     '#7F77DD',
  chart2:     '#1D9E75',
  chart3:     '#BA7517',
  chart4:     '#E05252',
};

// ── STYLE HELPERS ────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: C.card,
  border: `0.5px solid ${C.cardBorder}`,
  borderRadius: 10,
  padding: '14px 16px',
};

const statCard: React.CSSProperties = {
  ...card,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const tabBtn = (active: boolean): React.CSSProperties => ({
  fontSize: 12,
  padding: '5px 16px',
  borderRadius: '6px 6px 0 0',
  border: `0.5px solid ${active ? C.border : 'transparent'}`,
  cursor: 'pointer',
  background: active ? '#1e1e24' : 'transparent',
  color: active ? C.text : C.textSub,
  fontWeight: active ? 500 : 400,
  fontFamily: 'inherit',
  transition: 'all 0.15s',
});

const pill = (color: string, bg: string): React.CSSProperties => ({
  fontSize: 10, padding: '2px 8px', borderRadius: 20,
  background: bg, color, fontWeight: 500, display: 'inline-block',
});

// ── TYPES ────────────────────────────────────────────────────────────────────
interface Trade {
  id: string;
  symbol: string;
  type: 'buy' | 'sell';
  openDate: string;   // ISO
  closeDate?: string | null;
  openPrice: number;
  closePrice?: number | null;
  stopPrice?: number | null;
  initialStopPrice?: number | null;
  targetPrice?: number | null;
  rr?: number | null;
  outcome?: 'win' | 'loss' | 'be';
  strategyMatch?: boolean | null;
  strategy_assessed?: boolean | null;
  strategy_status?: string | null;
  strategy_comparison_state?: string | null;
  strategy_comparison?: StrategyComparison | null;
  notes?: string;
  session: string;   // e.g. "Monday AM"
  // Backend sends these on every trade; typed here so consumers stop casting through `any`.
  riskPct?: number;      // % of account risked (e.g. 0.5 = 0.5%)
  sessionId?: string;
  accountId?: string | null;
  tradingApproach?: string;
  backtestId?: string;   // MANUAL-logged trades only — auto trades omit it. Never key aggregation on it.
  strategy?: string;     // Strategy name — blank on legacy rows that predate it
  status?: string | null;
  recorded_at?: string;  // ISO — server-side write timestamp
}

interface JournalEntry {
  strategy_comparison?: StrategyComparison | null;
  id: string;
  date: string;
  type: 'trade' | 'miss' | 'psychology' | 'summary' | 'weekly';
  content: string;
  tradeId?: string;
  sessionId?: string;
  recorded_at?: string;  // ISO — server-side write stamp; the spine the Journals box sorts on
}

interface BacktestData {
  id: string;
  name: string;
  description?: string;
  trades: Trade[];
  notes?: string;
  createdAt: string;
  accountSize?: number;  // Per-backtest account size — independent from live
}

export interface ArthurSessionContext {
  mode: 'general' | 'live' | 'backtesting' | 'demo';
  sessionId?: string;
  instrument?: 'XAUUSD' | 'NASDAQ';
  approach?: 'main' | 'discretionary';
  accountId?: string | null;
  backtestId?: string;
  backtestName?: string;
}
interface ArthurLiveSession extends ArthurSessionContext { id: string; active: boolean; startedAt: string; }

interface DashboardData {
  accountSize: number;
  startDate: string;
  endDate?: string;
  trades: Trade[];
  journal: JournalEntry[];
  backtests: BacktestData[];
  sessionDate?: string;
  liveSessionActive: boolean;
  liveSessions?: Record<string, ArthurLiveSession>;
  selectedContext?: ArthurSessionContext;
  availableAccountIds?: string[];
  contextThreadId?: string;
}

type ArthurSurface = 'HOME' | 'LIVE_SESSION' | 'BACKTESTING' | 'LEDGER' | 'DREAMS_AND_JOURNALS' | 'DEEP_EXPERTISE';

type TradingTab = 'home' | 'live' | 'backtesting' | 'ledger' | 'dreams' | 'expertise';

const SURFACE_BY_TAB: Record<TradingTab, ArthurSurface> = {
  home: 'HOME',
  live: 'LIVE_SESSION',
  backtesting: 'BACKTESTING',
  ledger: 'LEDGER',
  dreams: 'DREAMS_AND_JOURNALS',
  expertise: 'DEEP_EXPERTISE',
};

interface TradingPageProps {
  authToken: string | null;
  onBack: () => void;
  threadId?: string | null;
  onActiveSurfaceChange?: (surface: ArthurSurface) => void;
  onSessionContextChange?: (context: ArthurSessionContext) => void;
}

// ── HELPERS ──────────────────────────────────────────────────────────────────
function fmt$(n: number) {
  return n >= 0
    ? `+$${n.toFixed(2)}`
    : `-$${Math.abs(n).toFixed(2)}`;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function fmtRR(n: unknown) {
  const value = finiteNumber(n);
  if (value === null) return '—';
  return value >= 0 ? `+${value.toFixed(2)}R` : `${value.toFixed(2)}R`;
}

function fmtOptionalNumber(value: unknown) {
  const n = finiteNumber(value);
  if (n === null) return '—';
  return Number.isInteger(n) ? n.toString() : n.toString();
}

function fmtPct(value: unknown) {
  const n = finiteNumber(value);
  if (n === null) return '—';
  return `${Number.isInteger(n) ? n.toString() : n.toString()}%`;
}

function rrColor(value: unknown) {
  const n = finiteNumber(value);
  if (n === null) return C.textMuted;
  return n >= 0 ? C.green : C.red;
}

// ── BACKTEST OVERVIEW AGGREGATION ───────────────────────────────────────────
// Pure client-side roll-up over data.backtests. Backtesting is PRESENT-ONLY — there is
// no nightly worker and no roll-up store; every number below is recomputed on render
// from the trades already in state.

type Grade = 'A+' | 'A' | 'B+' | 'B' | 'C';

const GRADE_ORDER: Grade[] = ['A+', 'A', 'B+', 'B', 'C'];

// teal → green → yellow → amber → red
const GRADE_COLOR: Record<Grade, string> = {
  'A+': '#22A79B',
  'A':  C.green,
  'B+': '#C8A82E',
  'B':  C.amber,
  'C':  C.red,
};

// THE scored predicate. The backend serialises an UNSCORED rr as JSON `null`, so
// `rr !== undefined` counts unscored rows as scored → inflated counts and a NaN average.
// Everything downstream (setups-scored, the win-rate denominator, avg RR, grading)
// keys on this one function.
export function isScored(t: Trade): boolean {
  return typeof t.rr === 'number' && Number.isFinite(t.rr);
}

export type StrategyDisplayState = 'pending' | 'unscored' | 'aligned' | 'deviated';
interface StrategyComparison {
  trade_id?: string;
  state: StrategyDisplayState;
  scope?: string;
  dimensions: { rule: string; expected: unknown; actual: unknown; state: StrategyDisplayState }[];
  unknowns?: string[];
}


function strategyStatus(t: Trade): string {
  const raw = t.strategy_status
    ?? (t as any).strategyStatus
    ?? t.strategy_comparison_state
    ?? (t as any).strategyComparisonState
    ?? '';
  return String(raw).trim().toUpperCase();
}

function isPendingStrategyStatus(status: string): boolean {
  return status.includes('PENDING')
    || status.includes('UNAVAILABLE')
    || status.includes('MISSING')
    || status.includes('ABSENT');
}

function isCompletedStrategyStatus(status: string): boolean {
  return status.includes('COMPLETE')
    || status === 'ASSESSED'
    || status === 'SCORED'
    || status === 'ALIGNED'
    || status === 'ADHERENT'
    || status === 'DEVIATED'
    || status === 'DIVERGED';
}

export function strategyDisplayStateFor(t: Trade, doctrineInstalled = false): StrategyDisplayState {
  if (doctrineInstalled && t.strategy_assessed !== true) return t.strategy_comparison?.state || 'unscored';
  const status = strategyStatus(t);
  if (status && isPendingStrategyStatus(status)) return 'pending';
  if (!status && t.strategy_assessed !== true) return 'pending';

  const completedComparison = t.strategy_assessed === true || isCompletedStrategyStatus(status);
  if (completedComparison) {
    if (status === 'ALIGNED' || status === 'ADHERENT') return 'aligned';
    if (status === 'DEVIATED' || status === 'DIVERGED') return 'deviated';
    if (t.strategyMatch === true) return 'aligned';
    if (t.strategyMatch === false) return 'deviated';
  }
  return 'unscored';
}

export function isStrategyCompared(t: Trade): boolean {
  const state = strategyDisplayStateFor(t);
  return state === 'aligned' || state === 'deviated';
}

export function strategyDisplayLabelForState(state: StrategyDisplayState): string {
  if (state === 'aligned') return '✓ Aligned';
  if (state === 'deviated') return '✗ Deviated';
  if (state === 'unscored') return 'Unscored';
  return 'Pending';
}

export function strategyDisplayLabelFor(t: Trade): string {
  return strategyDisplayLabelForState(strategyDisplayStateFor(t));
}

function strategyDisplayPillStyle(state: StrategyDisplayState): React.CSSProperties {
  if (state === 'aligned') return pill(C.greenDark, C.greenLight);
  if (state === 'deviated') return pill(C.redDark, C.redLight);
  if (state === 'unscored') return pill(C.textSub, 'rgba(255,255,255,0.08)');
  return pill(C.amber, C.amberLight);
}

function hobartDayKey(value: unknown): string {
  if (!value) return '';
  const ts = Date.parse(String(value));
  if (!Number.isFinite(ts)) return '';
  return new Date(ts).toLocaleDateString('en-AU', { timeZone: 'Australia/Hobart' });
}

export function isOpenLivePosition(t: Trade): boolean {
  const status = String(t.status || '').trim().toLowerCase();
  return status === 'open' || (!t.closeDate && t.closePrice == null && !isScored(t));
}

function isTradeOnHobartDay(t: Trade, dayKey: string): boolean {
  return [t.openDate, t.closeDate, (t as any).recorded_at]
    .some(value => hobartDayKey(value) === dayKey);
}

export function liveSessionTradesFor(trades: Trade[], now: Date = new Date()): Trade[] {
  const dayKey = now.toLocaleDateString('en-AU', { timeZone: 'Australia/Hobart' });
  return trades.filter(t => isOpenLivePosition(t) || isTradeOnHobartDay(t, dayKey));
}

export function homeClosedTradesFor(trades: Trade[]): Trade[] {
  return trades.filter(t => !!t.closeDate && isScored(t));
}

// Derived grade — two axes: strategyMatch and the RR band. Unmatched shifts down one
// tier, so a matched loss is B (plan followed, market disagreed) and an unmatched loss
// is C (plan broken). Unscored rows return null: no grade, excluded from every
// aggregate, NOT bucketed as C.
function gradeOf(t: Trade): Grade | null {
  if (!isScored(t)) return null;
  const rr = t.rr as number;
  if (t.strategyMatch === true) {
    if (rr >= 2) return 'A+';
    if (rr >= 1) return 'A';
    if (rr >= 0) return 'B+';
    return 'B';
  }
  if (rr >= 2) return 'A';
  if (rr >= 1) return 'B+';
  if (rr >= 0) return 'B';
  return 'C';
}

// One flattened library row per trade. Strategy comes from the PARENT backtest name —
// trades are nested inside their backtest, and t.backtestId is manual-only.
interface OverviewRow {
  trade: Trade;
  backtestId: string;
  backtestName: string;
  grade: Grade | null;
  scored: boolean;
  date: string;   // openDate, falling back to closeDate
}

function overviewRows(backtests: BacktestData[]): OverviewRow[] {
  const rows: OverviewRow[] = [];
  backtests.forEach(bt => {
    (bt.trades || []).forEach(t => {
      rows.push({
        trade: t,
        backtestId: bt.id,
        backtestName: bt.name,
        grade: gradeOf(t),
        scored: isScored(t),
        date: t.openDate || t.closeDate || '',
      });
    });
  });
  return rows;
}

// The four header cards.
function overviewTotals(backtests: BacktestData[], rows: OverviewRow[]) {
  const scored = rows.filter(r => r.scored);
  const wins = scored.filter(r => r.trade.outcome === 'win');
  const rrSum = scored.reduce((s, r) => s + (r.trade.rr as number), 0);
  return {
    backtestsRun: backtests.length,
    setupsScored: scored.length,
    winRate: scored.length ? (wins.length / scored.length) * 100 : null,
    avgRR: scored.length ? rrSum / scored.length : null,
    wins: wins.length,
    unscored: rows.length - scored.length,
  };
}

// Distribution + the stats-by-grade table read off the same grouping.
function overviewByGrade(rows: OverviewRow[]) {
  return GRADE_ORDER.map(grade => {
    const g = rows.filter(r => r.grade === grade);
    const w = g.filter(r => r.trade.outcome === 'win').length;
    const rrSum = g.reduce((s, r) => s + (r.trade.rr as number), 0);
    return {
      grade,
      n: g.length,
      winPct: g.length ? (w / g.length) * 100 : null,
      avgR: g.length ? rrSum / g.length : null,
      color: GRADE_COLOR[grade],
    };
  });
}

// Context strip — instrument · strategy · replay period, all read off real rows.
function overviewContext(backtests: BacktestData[], rows: OverviewRow[]) {
  const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean)));
  const symbols = uniq(rows.map(r => r.trade.symbol));
  const strategies = uniq(backtests.map(bt => bt.name));
  const times = rows
    .map(r => new Date(r.trade.closeDate || r.date).getTime())
    .filter(n => Number.isFinite(n));
  const d = (ms: number) =>
    new Date(ms).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return {
    instrument: symbols.length === 0 ? '—'
      : symbols.length === 1 ? symbols[0]
      : `${symbols.length} instruments`,
    strategy: strategies.length === 0 ? '—'
      : strategies.length === 1 ? strategies[0]
      : `${strategies.length} strategies`,
    period: times.length === 0 ? '—'
      : times.length === 1 ? d(times[0])
      : `${d(Math.min(...times))} → ${d(Math.max(...times))}`,
  };
}

// Simple, real filter over library rows: whitespace-separated terms, every term must
// appear somewhere in the row (grade · symbol · strategy · session · outcome · date).
// Natural-language parsing ("after a midday loss") is deferred — this matches text only.
function matchesOverviewQuery(r: OverviewRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const iso = r.date ? r.date.slice(0, 10) : '';
  const pretty = r.date
    ? new Date(r.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const hay = [
    r.grade || 'ungraded',
    r.trade.symbol,
    r.backtestName,
    r.trade.session,
    r.trade.outcome,
    r.trade.notes,
    iso,
    pretty,
  ].filter(Boolean).join(' ').toLowerCase();
  return q.split(/\s+/).every(term => hay.includes(term));
}

// ── LEDGER AGGREGATION ──────────────────────────────────────────────────────
// Per-trade log over the trader's OWN trades (data.trades — the dashboard store).
// Read-only: this tab renders the store, it never writes it. Rows and the four period
// cards are computed client-side from the same trade source the rest of the page
// reads; when Forge's read-only mirror lands (GET /api/arthur/ledger, precomputed
// rollups, materialised nightly to trades_2026.xlsx) the fetch swaps to that endpoint
// and the served rollups render directly — same math, same shape.

interface LedgerRow {
  trade: Trade;
  ts: number;         // epoch ms of the timestamp spine
  legacy: boolean;    // no recorded_at — row sits on closeDate || openDate
  grade: Grade | null;
  scored: boolean;
  dayKey: string;     // Hobart-local calendar day — the stacking key
}

// The timestamp spine: recorded_at (the server-side write stamp) is the sort key;
// closeDate || openDate is the fallback for legacy rows that predate the stamp.
function ledgerRows(trades: Trade[]): LedgerRow[] {
  return trades
    .map(t => {
      const src = t.recorded_at || t.closeDate || t.openDate || '';
      const parsed = Date.parse(src);
      const ts = Number.isFinite(parsed) ? parsed : 0;  // unparseable sinks to the bottom, never dropped
      return {
        trade: t,
        ts,
        legacy: !t.recorded_at,
        grade: gradeOf(t),
        scored: isScored(t),
        dayKey: ts
          ? new Date(ts).toLocaleDateString('en-AU', { timeZone: 'Australia/Hobart' })
          : 'Undated',
      };
    })
    .sort((a, b) => b.ts - a.ts);   // newest first
}

// The four period cards — same scored predicate and same math as the Overview cards.
function ledgerTotals(rows: LedgerRow[]) {
  const scored = rows.filter(r => r.scored);
  const wins = scored.filter(r => r.trade.outcome === 'win');
  const netR = scored.reduce((s, r) => s + (r.trade.rr as number), 0);
  return {
    trades: rows.length,
    netR: scored.length ? netR : null,
    winRate: scored.length ? (wins.length / scored.length) * 100 : null,
    avgR: scored.length ? netR / scored.length : null,
    wins: wins.length,
    scored: scored.length,
    unscored: rows.length - scored.length,
  };
}

// "Ask this log" — the same simple text filter the Overview uses: whitespace-separated
// terms, every term must appear somewhere in the row (grade · symbol · session ·
// outcome · date · strategy). Smart/grounded querying is Arthur-in-conversation, not
// this box — this matches text only.
function matchesLedgerQuery(r: LedgerRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const iso = r.ts ? new Date(r.ts).toISOString().slice(0, 10) : '';
  const pretty = r.ts
    ? new Date(r.ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Hobart' })
    : '';
  const hay = [
    r.grade || 'ungraded',
    r.trade.symbol,
    r.trade.session,
    r.trade.outcome,
    r.trade.strategy,
    iso,
    pretty,
    r.dayKey,
  ].filter(Boolean).join(' ').toLowerCase();
  return q.split(/\s+/).every(term => hay.includes(term));
}

// ── LEDGER MIRROR (GET /api/arthur/ledger — server/ledger_store.py) ─────────
// The real flow: dashboard store (SSOT) → nightly/lazy materialisation → mirror →
// this tab. Rows arrive newest-first on the recorded_at spine (date/time are that
// stamp split), grades are derived AT THE MIRROR (same rule as gradeOf), and the
// rollups block is precomputed at the top — the cards render it directly.
// Client-side compute over data.trades remains ONLY as the graceful fallback
// while the mirror is loading or unavailable.

interface LedgerMirrorRow {
  trade_id: string;
  session_id?: string;
  account_id?: string;
  date: string;          // YYYY-MM-DD of the recorded_at spine (openDate fallback for legacy)
  time: string;          // HH:MM:SS of the same stamp
  entry_timestamp?: string | null;
  exit_timestamp?: string | null;
  session: string;
  instrument: string;
  direction: string;     // 'buy' | 'sell'
  strategy: string;
  setup_grade: string | null;
  entry: number | null;
  exit: number | null;
  size: number | null;
  R_multiple: number | null;
  outcome: string;
  fundamental_flag: boolean | null;
  notes: string;
}

interface LedgerMirror {
  generated_at: string;
  source: string;
  rollups: {
    period: { from: string | null; to: string | null };
    trades: number;
    scored: number;
    unscored: number;
    net_r: number | null;
    win_rate: number | null;
    avg_r: number | null;
    by_grade: { grade: string; n: number; win_pct: number | null; avg_r: number | null }[];
  };
  rows: LedgerMirrorRow[];
}

// Mirror row → the same LedgerRow shape the table renders, so the cleared render
// is untouched by the source swap. Grade comes from the mirror (server-derived),
// never re-derived here. The date/time strings are the user's world clock already.
function ledgerRowsFromMirror(mirrorRows: LedgerMirrorRow[]): LedgerRow[] {
  return mirrorRows
    .map(m => {
      const rr = typeof m.R_multiple === 'number' && Number.isFinite(m.R_multiple)
        ? m.R_multiple : undefined;
      const parsed = Date.parse(m.time ? `${m.date}T${m.time}` : m.date);
      const ts = Number.isFinite(parsed) ? parsed : 0;
      const trade: Trade = {
        id: m.trade_id,
        sessionId: m.session_id,
        accountId: m.account_id,
        symbol: m.instrument,
        type: m.direction === 'sell' ? 'sell' : 'buy',
        openDate: m.entry_timestamp || m.date,
        closeDate: m.exit_timestamp || undefined,
        openPrice: m.entry ?? 0,
        closePrice: m.exit ?? undefined,
        rr,
        outcome: (m.outcome || undefined) as Trade['outcome'],
        strategyMatch: false,   // not mirrored — grade below is the server-derived truth
        notes: m.notes || undefined,
        session: m.session || '',
        strategy: m.strategy || undefined,
      };
      return {
        trade,
        ts,
        legacy: false,   // the mirror does not carry the legacy marker — no asterisk on mirror rows
        grade: (GRADE_ORDER as string[]).includes(m.setup_grade || '')
          ? (m.setup_grade as Grade) : null,
        scored: rr !== undefined,
        dayKey: m.date || 'Undated',
      };
    })
    .sort((a, b) => b.ts - a.ts);   // mirror is already newest-first; defensive re-sort, same key
}

// ── ARTHUR'S DREAMS + JOURNALS ─────────────────────────────────────────────
// The psychology surface, and a HALLUCINATION CHECK: everything here is
// time-threaded, so every row carries its own visible stamp. An entry with no
// stamp did not happen on the record — the missing stamp is surfaced LOUDLY
// (never a blank space), because a silent gap is exactly what this tab exists
// to catch. Read-only on both boxes: dreams come from GET /api/arthur/dreams
// (the 02:00 pass writes them), journals from the dashboard payload.

interface DreamInsight extends DreamReviewInsight {
  id: string;
  created_at: string | null;
  night: string | null;     // "YYYY-MM-DD" — the covered night
  content: string;          // what Arthur wrote, VERBATIM — never reshaped here
}

// The last artifact the dream produced: when it fired and what window it covered.
interface DreamRunView {
  run_id: string | null;
  night: string | null;
  status: string | null;
  started_at: string | null;
  completed_at: string | null;
  window_start: string | null;
  window_end: string | null;
  insights: number;
  error: string | null;
}

interface DreamsView {
  insights: DreamInsight[];      // newest-first by created_at (server-sorted)
  last_run: DreamRunView | null;
  insights_status: string;       // ok | fresh | unreadable — passed through, never flattened
  store_status: string;
  ledger_status: string;
  degraded: boolean;
}

interface DoctrineStatus {
  status?: string;
  package_t1_count?: number;
  strategy_routing_status?: string;
  psychology_routing_status?: string;
  strategy_substantive_status?: string;
  strategy_version?: string | null;
  psychology_substantive_status?: string;
  strategy_label?: string;
  psychology_label?: string;
  strategy_leading_document?: string | null;
  psychology_leading_document?: string | null;
  real_psychology_doctrine_count?: number;
  strategy_destination?: string;
  psychology_destination?: string;
}

// Night grouping (DreamstatePanel's shape) — the insights arrive FLAT and
// already newest-first, so grouping walks them in order and never re-sorts.
// A row with no night falls back to its stamp's date; neither → 'Undated',
// which is itself a visible tell.
function dreamNights(insights: DreamInsight[]): { night: string; rows: DreamInsight[] }[] {
  const groups: { night: string; rows: DreamInsight[] }[] = [];
  insights.forEach(ins => {
    const night = ins.night
      || (ins.created_at ? String(ins.created_at).slice(0, 10) : 'Undated');
    const last = groups[groups.length - 1];
    if (last && last.night === night) last.rows.push(ins);
    else groups.push({ night, rows: [ins] });
  });
  return groups;
}

interface JournalRow {
  entry: JournalEntry;
  ts: number;
  stamped: boolean;    // false → the hallucination tell, rendered loudly
  spine: 'recorded_at' | 'date';
}

// Journals sorted on recorded_at (the write spine), falling back to `date` for
// legacy rows. Deliberately NOT the Live Session Journal component — that one
// sorts on `date` only, which is the entry's own day, not when it was written.
function journalRows(journal: JournalEntry[]): JournalRow[] {
  return [...(journal || [])]
    .map(e => {
      const src = e.recorded_at || e.date || '';
      const parsed = Date.parse(src);
      return {
        entry: e,
        ts: Number.isFinite(parsed) ? parsed : 0,   // unparseable sinks last, never dropped
        stamped: !!src && Number.isFinite(parsed),
        spine: (e.recorded_at ? 'recorded_at' : 'date') as 'recorded_at' | 'date',
      };
    })
    .sort((a, b) => b.ts - a.ts);
}

function sessionLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const day = days[d.getDay()];
  const h = d.getHours();
  return `${day} ${h < 12 ? 'AM' : 'PM'}`;
}

// Half-donut chart (pure SVG)
function HalfDonut({ wins, losses, size = 120 }: { wins: number; losses: number; size?: number }) {
  const total = wins + losses || 1;
  const winPct = Math.min(Math.max(wins / total, 0), 1);
  const r = 42;
  const cx = size / 2;
  const cy = size / 2 + 8;
  const stroke = 11;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const arc = (pct: number, startDeg: number) => {
    // Clamp to avoid degenerate arcs at exactly 0 or 1
    const safePct = Math.min(Math.max(pct, 0.001), 0.999);
    const endDeg = startDeg + safePct * 180;
    const x1 = cx + r * Math.cos(toRad(180 - startDeg));
    const y1 = cy - r * Math.sin(toRad(180 - startDeg));
    const x2 = cx + r * Math.cos(toRad(180 - endDeg));
    const y2 = cy - r * Math.sin(toRad(180 - endDeg));
    const large = safePct > 0.5 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };
  const winPath = arc(winPct, 0);
  const losePath = arc(1 - winPct, winPct * 180);
  return (
    <svg width={size} height={size / 2 + 18} viewBox={`0 0 ${size} ${size / 2 + 18}`} style={{ overflow: 'hidden' }}>
      <path d={arc(1, 0)} fill="none" stroke={C.cardBorder} strokeWidth={stroke} strokeLinecap="round" />
      {wins > 0 && <path d={winPath} fill="none" stroke={C.green} strokeWidth={stroke} strokeLinecap="round" />}
      {losses > 0 && <path d={losePath} fill="none" stroke={C.red} strokeWidth={stroke} strokeLinecap="round" />}
      <text x={cx} y={cy - 4} textAnchor="middle" fill={C.text} fontSize={13} fontWeight={600}>
        {(winPct * 100).toFixed(1)}%
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill={C.textSub} fontSize={10}>
        Win Rate
      </text>
    </svg>
  );
}

// ── CUSTOM TOOLTIP ───────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e1e28', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: C.textSub, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color, fontWeight: 500 }}>
          {p.name}: ${p.value?.toFixed(2)}
        </div>
      ))}
    </div>
  );
}

// ── EMPTY STATE ──────────────────────────────────────────────────────────────
function EmptyState({ label }: { label: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 0', color: C.textMuted, fontSize: 12 }}>
      {label}
    </div>
  );
}

// ── STAT CARD ────────────────────────────────────────────────────────────────
function StatTile({ label, value, color, sub }: { label: string; value: string; color?: string; sub?: string }) {
  return (
    <div style={statCard}>
      <div style={{ fontSize: 10, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.textMuted }}>{sub}</div>}
    </div>
  );
}

// ── EQUITY CURVE ─────────────────────────────────────────────────────────────
function EquityCurve({ trades, accountSize, height = 220, showStrategy = false }: {
  trades: Trade[];
  accountSize: number;
  height?: number;
  showStrategy?: boolean;
}) {
  if (trades.length === 0) return <EmptyState label="No trades yet — equity curve will appear here" />;
  let balance = accountSize;
  const data = trades
    .filter(t => t.closeDate && t.rr !== undefined)
    .map(t => {
      // P&L = accountSize × riskPct% × RR
      // riskPct is the % risked (e.g. 0.5 = 0.5%), rr is signed (+3 win, -1 loss)
      const riskPct = (t as any).riskPct != null && (t as any).riskPct > 0 ? (t as any).riskPct : 0.5;
      const riskDollar = accountSize * (riskPct / 100);
      const pnl = (t.rr || 0) * riskDollar;
      balance += pnl;
      return {
        date: new Date(t.closeDate!).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        Balance: parseFloat(balance.toFixed(2)),
        Equity: parseFloat(balance.toFixed(2)),
      };
    });
  if (data.length === 0) return <EmptyState label="Close trades to see equity curve" />;
  const allValues = data.map(d => d.Balance);
  const minVal = Math.min(...allValues, accountSize);
  const maxVal = Math.max(...allValues, accountSize);
  const padding = Math.max((maxVal - minVal) * 0.2, accountSize * 0.005);
  const yMin = Math.floor((minVal - padding) / 100) * 100;
  const yMax = Math.ceil((maxVal + padding) / 100) * 100;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.cardBorder} />
        <XAxis dataKey="date" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} width={65}
          domain={[yMin, yMax]}
          tickFormatter={(v: number) => `$${(v/1000).toFixed(1)}k`} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11, color: C.textSub }} />
        <Line type="monotone" dataKey="Balance" stroke={C.chart1} strokeWidth={1.5} dot={{ r: 2, fill: C.chart1 }} />
        <Line type="monotone" dataKey="Equity" stroke={C.chart2} strokeWidth={1.5} dot={{ r: 2, fill: C.chart2 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── TRADE TABLE ──────────────────────────────────────────────────────────────
export function TradeTable({ trades, onDelete, showLiveStateColumns = false, doctrineInstalled = false }: { trades: Trade[]; onDelete?: (id: string) => void; showLiveStateColumns?: boolean; doctrineInstalled?: boolean }) {
  if (trades.length === 0) return <EmptyState label="No trades logged yet" />;
  const showSessionIdentity = trades.some(t => t.sessionId);
  const sorted = [...trades].sort((a, b) => new Date(b.openDate).getTime() - new Date(a.openDate).getTime());
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {[
              'Symbol', 'Type', 'Session', ...(showSessionIdentity ? ['Live Session / Account'] : []), 'Open', 'Close', 'Open Price',
              ...(showLiveStateColumns ? ['Stop', 'Risk %', 'Status'] : []),
              'Close Price', 'RR', 'Strategy', ...(onDelete ? [''] : []),
            ].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.textMuted, fontWeight: 500, borderBottom: `0.5px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((t, i) => (
            <tr key={t.id} style={{ borderBottom: `0.5px solid ${C.cardBorder2}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
              <td style={{ padding: '8px 10px', color: C.text, fontWeight: 500 }}>{t.symbol}</td>
              <td style={{ padding: '8px 10px' }}>
                <span style={pill(t.type === 'buy' ? C.greenDark : C.redDark, t.type === 'buy' ? C.greenLight : C.redLight)}>
                  {t.type.toUpperCase()}
                </span>
              </td>
              <td style={{ padding: '8px 10px', color: C.textSub }}>{t.session}</td>
              {showSessionIdentity && <td style={{ padding: '8px 10px', color: C.textSub }}>{t.sessionId ? `${t.sessionId.slice(-6)} · ${t.accountId || 'Unassigned'}` : '—'}</td>}
              <td style={{ padding: '8px 10px', color: C.textSub }}>
                {new Date(t.openDate).toLocaleString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </td>
              <td style={{ padding: '8px 10px', color: C.textSub }}>
                {t.closeDate ? new Date(t.closeDate).toLocaleString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </td>
              <td style={{ padding: '8px 10px', color: C.text }}>{t.openPrice}</td>
              {showLiveStateColumns && (
                <>
                  <td style={{ padding: '8px 10px', color: C.text }}>{fmtOptionalNumber(t.stopPrice)}</td>
                  <td style={{ padding: '8px 10px', color: C.textSub }}>{fmtPct(t.riskPct)}</td>
                  <td style={{ padding: '8px 10px', color: C.textSub }}>{String(t.status || (t.closeDate ? 'closed' : 'open')).toUpperCase()}</td>
                </>
              )}
              <td style={{ padding: '8px 10px', color: C.text }}>{t.closePrice || '—'}</td>
              <td style={{ padding: '8px 10px', fontWeight: 600, color: rrColor(t.rr) }}>
                {fmtRR(t.rr)}
              </td>
              <td style={{ padding: '8px 10px' }}>
                {t.tradingApproach === 'discretionary' ? <span style={{ color: C.textSub }}>Discretionary</span> : (() => {
                  const state = strategyDisplayStateFor(t, doctrineInstalled);
                  return <span style={strategyDisplayPillStyle(state)}>{strategyDisplayLabelForState(state)}</span>;
                })()}
              </td>
              {onDelete && (
                <td style={{ padding: '8px 10px' }}>
                  <button
                    onClick={() => onDelete(t.id)}
                    title="Delete trade"
                    style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: `0.5px solid ${C.redDark}`, background: 'transparent', color: C.red, cursor: 'pointer', fontFamily: 'inherit' }}
                  >✕</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── JOURNAL ──────────────────────────────────────────────────────────────────
function StrategyComparisonDetails({ comparison }: { comparison?: StrategyComparison | null }) {
  if (!comparison?.dimensions?.length) return null;
  return <div style={{ marginTop: 8 }}>
    <div style={{ fontSize: 11, color: C.textMuted }}>Entry rules only; later management and outcome remain separate.</div>
    <table style={{ width: '100%', fontSize: 12 }}><thead><tr><th>Rule</th><th>Required</th><th>Reported</th><th>Comparison</th></tr></thead>
      <tbody>{comparison.dimensions.map(d => <tr key={d.rule}><td>{d.rule}</td><td>{d.expected == null ? 'Unknown' : String(d.expected)}</td><td>{d.actual == null ? 'Unknown' : String(d.actual)}</td><td>{strategyDisplayLabelForState(d.state)}</td></tr>)}</tbody>
    </table>
  </div>;
}

function Journal({ entries }: { entries: JournalEntry[] }) {
  if (entries.length === 0) return <EmptyState label="Arthur's journal entries will appear here during live sessions" />;
  const sorted = [...entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const typeStyle: Record<string, { color: string; bg: string; label: string }> = {
    trade:      { color: C.greenDark, bg: C.greenLight, label: 'Trade' },
    miss:       { color: C.redDark, bg: C.redLight, label: 'Missed' },
    psychology: { color: '#3C3489', bg: '#EEEDFE', label: 'Psychology' },
    summary:    { color: C.amber, bg: C.amberLight, label: 'Day Summary' },
    weekly:     { color: '#2a5080', bg: '#d6eaf8', label: 'Week Summary' },
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {sorted.map(e => {
        const ts = typeStyle[e.type] || typeStyle.trade;
        return (
          <div key={e.id} style={{ ...card, display: 'flex', gap: 12, padding: '12px 14px' }}>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              <span style={pill(ts.color, ts.bg)}>{ts.label}</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>
                {new Date(e.date).toLocaleString('en-AU', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{e.content}</div>
              <StrategyComparisonDetails comparison={e.strategy_comparison} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── RADAR SCORE — pure SVG, no recharts dependency ──────────────────────────
function ScoreRadar({ trades }: { trades: Trade[] }) {
  const wins = trades.filter(t => t.outcome === 'win').length;
  const total = trades.length || 1;
  const winRate = wins / total;
  const strategyMatches = trades.filter(t => t.strategyMatch).length / total;
  const avgRR = trades.filter(t => t.rr !== undefined).reduce((s, t) => s + (t.rr || 0), 0) / (trades.filter(t => t.rr !== undefined).length || 1);
  const consistency = strategyMatches;
  const score = Math.round((consistency * 25 + Math.min(1, avgRR / 3) * 25 + winRate * 25 + 0.85 * 25) * 100) / 100;

  const axes = [
    { label: 'Consistency', value: consistency },
    { label: 'RR', value: Math.min(1, avgRR / 3) },
    { label: 'SL Usage', value: 0.85 },
    { label: 'Win Rate', value: winRate },
  ];

  const cx = 100; const cy = 90; const r = 60;
  const n = axes.length;
  const toPoint = (i: number, pct: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return {
      x: cx + r * pct * Math.cos(angle),
      y: cy + r * pct * Math.sin(angle),
    };
  };
  const gridLevels = [0.25, 0.5, 0.75, 1];
  const dataPoints = axes.map((a, i) => toPoint(i, a.value));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <div style={{ ...card, background: 'linear-gradient(135deg, #1a1840 0%, #111228 100%)', position: 'relative' }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 4 }}>Score</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width={200} height={160} viewBox="0 0 200 160">
          {/* Grid circles */}
          {gridLevels.map(lvl => {
            const pts = axes.map((_, i) => toPoint(i, lvl));
            const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
            return <path key={lvl} d={path} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={0.5} />;
          })}
          {/* Axis lines */}
          {axes.map((_, i) => {
            const outer = toPoint(i, 1);
            return <line key={i} x1={cx} y1={cy} x2={outer.x} y2={outer.y} stroke="rgba(255,255,255,0.06)" strokeWidth={0.5} />;
          })}
          {/* Data polygon */}
          <path d={dataPath} fill="rgba(175,169,236,0.2)" stroke="#AFA9EC" strokeWidth={1.5} />
          {/* Axis labels */}
          {axes.map((a, i) => {
            const pt = toPoint(i, 1.25);
            return (
              <text key={i} x={pt.x} y={pt.y} textAnchor="middle" dominantBaseline="middle"
                fill="rgba(255,255,255,0.55)" fontSize={8} fontWeight={500}>
                {a.label}
              </text>
            );
          })}
        </svg>
        <div style={{ textAlign: 'right', paddingRight: 8 }}>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{score.toFixed(2)}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>Overall Score</div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {axes.map(a => (
              <div key={a.label} style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>{a.label}</span>
                <span style={{ color: '#AFA9EC' }}>{(a.value * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function TradingPage({ authToken, onBack, threadId, onActiveSurfaceChange, onSessionContextChange }: TradingPageProps) {
  const [tab, setTab] = useState<TradingTab>('home');
  const [data, setData] = useState<DashboardData>({
    accountSize: 10000,
    startDate: new Date().toISOString().slice(0, 10),
    trades: [],
    journal: [],
    backtests: [],
    liveSessionActive: false,
  });
  const [sessionBusy, setSessionBusy] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [backtestSaving, setBacktestSaving] = useState(false);
  const [sessionInstrument, setSessionInstrument] = useState<'XAUUSD' | 'NASDAQ'>('XAUUSD');
  const [sessionApproach, setSessionApproach] = useState<'main' | 'discretionary'>('main');
  const [sessionAccount, setSessionAccount] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedBacktest, setSelectedBacktest] = useState<string | null>(null);
  const [showNewBtInput, setShowNewBtInput] = useState(false);
  const [newBtName, setNewBtName] = useState('');
  // Backtesting Overview — setup-library filter (text only; NL parsing deferred)
  const [overviewQuery, setOverviewQuery] = useState('');
  // Ledger — period scope for the cards + "Ask this log" filter (text only; smart
  // querying is Arthur-in-conversation, not built here)
  const [ledgerQuery, setLedgerQuery] = useState('');
  const [ledgerPeriod, setLedgerPeriod] = useState<'7d' | '30d' | 'ytd' | 'all'>('all');
  // Ledger mirror — fetched when the tab opens (the route lazy-materialises on
  // first GET). null + !loading + err → graceful fallback to the live store.
  const [ledgerMirror, setLedgerMirror] = useState<LedgerMirror | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerErr, setLedgerErr] = useState<string | null>(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  // Dreams — fetched when the tab opens. A 'fresh' read (no file before the
  // first 02:00) is an honest empty, NOT an error; only a failed request is.
  const [dreams, setDreams] = useState<DreamsView | null>(null);
  const [dreamsLoading, setDreamsLoading] = useState(false);
  const [dreamsErr, setDreamsErr] = useState<string | null>(null);
  const [doctrine, setDoctrine] = useState<DoctrineStatus | null>(null);
  const [doctrineLoading, setDoctrineLoading] = useState(false);
  const [doctrineErr, setDoctrineErr] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '', end: '',
  });

  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  const activeSurface = SURFACE_BY_TAB[tab];
  const threadParams = useCallback(() => (
    threadId ? { params: { thread_id: threadId } } : undefined
  ), [threadId]);
  const withThread = useCallback(<T extends Record<string, unknown>>(payload: T): T & { thread_id?: string } => (
    threadId ? { ...payload, thread_id: threadId } : payload
  ), [threadId]);

  useEffect(() => { onActiveSurfaceChange?.(activeSurface); }, [activeSurface, onActiveSurfaceChange]);

  // ── TRADE ENTRY FORM — component level (hooks cannot be inside IIFE) ──────
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [tradeSaving, setTradeSaving] = useState(false);
  const [tradeForm, setTradeForm] = useState({
    symbol: '', type: 'buy' as 'buy' | 'sell',
    outcome: 'win' as 'win' | 'loss' | 'be',
    rr: '', riskPct: '', session: 'Monday AM', notes: '',
  });
  const SESSIONS = ['Monday AM','Monday PM','Tuesday AM','Tuesday PM',
    'Wednesday AM','Wednesday PM','Thursday AM','Thursday PM',
    'Friday AM','Friday PM','Saturday AM','Saturday PM','Sunday AM','Sunday PM'];

  async function deleteTrade(tradeId: string) {
    try {
      await axios.delete(`${API_BASE}/arthur/trade/${tradeId}`, threadParams());
      setData(prev => ({
        ...prev,
        trades: prev.trades.filter(t => t.id !== tradeId),
        backtests: prev.backtests.map(bt => ({
          ...bt,
          trades: bt.trades.filter(t => t.id !== tradeId),
        })),
      }));
      console.log('[ARTHUR] Trade deleted:', tradeId);
    } catch (e) {
      console.error('[ARTHUR] Delete failed:', e);
    }
  }

  async function submitTrade(btId: string, btName: string) {
    if (!tradeForm.symbol.trim() || !tradeForm.rr) return;
    setTradeSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/arthur/trade`, withThread({
        symbol: tradeForm.symbol.trim(),
        type: tradeForm.type,
        outcome: tradeForm.outcome,
        rr: parseFloat(tradeForm.rr) * (tradeForm.outcome === 'loss' ? -1 : 1),
        riskPct: parseFloat(tradeForm.riskPct) || 0,
        session: tradeForm.session,
        notes: tradeForm.notes,
        backtestId: btId,
        backtestName: btName,
        openDate: new Date().toISOString(),
        closeDate: new Date().toISOString(),
      }));
      if (res.data?.success) {
        setData(prev => ({
          ...prev,
          backtests: prev.backtests.map(b =>
            b.id === btId ? { ...b, trades: [...b.trades, res.data.trade] } : b
          ),
        }));
        setTradeForm({ symbol: '', type: 'buy', outcome: 'win', rr: '', riskPct: '', session: 'Monday AM', notes: '' });
        setShowTradeForm(false);
      }
    } catch (e) { console.error('[ARTHUR] Trade save failed:', e); }
    finally { setTradeSaving(false); }
  }

  async function resetDashboard() {
    setResetting(true);
    try {
      await axios.post(`${API_BASE}/arthur/dashboard/reset`, withThread({}));
      setData({
        ...data,
        accountSize: data.accountSize, // preserve account size on reset
        startDate: new Date().toISOString().slice(0, 10),
        endDate: '',
        liveSessionActive: false,
        trades: [],
        journal: [],
        backtests: [],
      });
      setResetConfirm(false);
    } catch (e) {
      console.error('[ARTHUR] Reset failed:', e);
    } finally {
      setResetting(false);
    }
  }

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/arthur/dashboard`, threadParams());
      if (res.data.success) {
        setData(res.data.data);
        if (res.data.data.selectedContext) onSessionContextChange?.(res.data.data.selectedContext);
      }
    } catch (e) {
      // Dashboard not yet seeded — use defaults
    } finally {
      setLoading(false);
    }
  }, [threadParams, onSessionContextChange]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const selectedContext = data.selectedContext || { mode: 'general' as const };
  const activeSessions = Object.values(data.liveSessions || {}).filter(session => session.active);
  useEffect(() => {
    if (selectedContext.mode === 'live') {
      setSessionInstrument(selectedContext.instrument || 'XAUUSD');
      setSessionApproach(selectedContext.approach || 'main');
      setSessionAccount(selectedContext.accountId || '');
    }
    if (selectedContext.mode === 'backtesting') setSelectedBacktest(selectedContext.backtestId || null);
  }, [selectedContext.mode, selectedContext.sessionId, selectedContext.instrument, selectedContext.approach, selectedContext.accountId, selectedContext.backtestId]);

  async function sessionControl(control: Record<string, unknown>) {
    setSessionBusy(true); setSessionError('');
    try {
      const res = await axios.post(`${API_BASE}/arthur/dashboard`, withThread({ sessionControl: control }));
      if (!res.data.success || !res.data.data?.selectedContext) throw new Error(res.data.error || 'Session controls are unavailable from this connection.');
      setData(res.data.data);
      onSessionContextChange?.(res.data.data.selectedContext);
    } catch (error: any) {
      setSessionError(error.response?.data?.error || error.message || 'Session selection failed');
    } finally { setSessionBusy(false); }
  }
  const activateSession = () => sessionControl({ op: 'activate', instrument: sessionInstrument,
    approach: sessionApproach, accountId: sessionAccount || null });
  function selectLiveDetails(update: Partial<ArthurSessionContext>) {
    if (update.instrument) setSessionInstrument(update.instrument);
    if (update.approach) setSessionApproach(update.approach);
    if (update.accountId !== undefined) setSessionAccount(update.accountId || '');
  }

  async function createBacktest() {
    const name = newBtName.trim();
    if (!name || backtestSaving) return;
    setBacktestSaving(true); setSessionError('');
    try {
      const res = await axios.post(`${API_BASE}/arthur/backtest`, withThread({ name }));
      if (!res.data?.success || !res.data.backtest?.id) throw new Error(res.data.error || 'The test could not be saved.');
      const backtest = res.data.backtest;
      setData(prev => ({ ...prev, backtests: [...prev.backtests, backtest] }));
      setShowNewBtInput(false); setNewBtName('');
      if (selectedContext.mode === 'backtesting') {
        await sessionControl({ op: 'backtesting', backtestId: backtest.id });
      } else {
        setSelectedBacktest(backtest.id);
      }
    } catch (error: any) {
      setSessionError(error.response?.data?.error || error.message || 'The test could not be saved.');
    } finally { setBacktestSaving(false); }
  }


  // ── LEDGER MIRROR FETCH ────────────────────────────────────────────────────
  // The bearer rides the global axios interceptor (App.tsx) — same as every
  // other call on this page.
  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/arthur/ledger`, threadParams());
      if (res.data?.success && res.data.ledger && Array.isArray(res.data.ledger.rows)) {
        setLedgerMirror(res.data.ledger);
        setLedgerErr(null);
      } else {
        setLedgerErr(res.data?.error || 'mirror unavailable');
      }
    } catch (e) {
      console.error('[ARTHUR] Ledger mirror fetch failed:', e);
      setLedgerErr('mirror unavailable');
    } finally {
      setLedgerLoading(false);
    }
  }, [threadParams]);

  useEffect(() => { if (tab === 'ledger') fetchLedger(); }, [tab, fetchLedger]);

  // ── DREAMS FETCH ───────────────────────────────────────────────────────────
  // Read-only. Bearer rides the global axios interceptor (App.tsx), same as
  // every other call on this page.
  const fetchDreams = useCallback(async () => {
    setDreamsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/arthur/dreams`, threadParams());
      if (res.data?.success) {
        setDreams(res.data as DreamsView);
        setDreamsErr(null);
      } else {
        setDreamsErr(res.data?.error || 'dreams unavailable');
      }
    } catch (e) {
      console.error('[ARTHUR] Dreams fetch failed:', e);
      setDreamsErr('dreams unavailable');
    } finally {
      setDreamsLoading(false);
    }
  }, [threadParams]);

  useEffect(() => { if (tab === 'dreams') fetchDreams(); }, [tab, fetchDreams]);

  const reviewDream = useCallback(async (insight: DreamInsight, action: 'confirm' | 'reject', reason: string) => {
    const result = await axios.post(`${API_BASE}/arthur/dreams/review`, {
      id: insight.id, revision: insight.revision, action, reason,
      expected_decision: insight.review?.decision_id || null,
    });
    if (!result.data?.success) throw new Error(result.data?.error || 'Decision not saved.');
    await fetchDreams();
  }, [fetchDreams]);


  // ── DEEP EXPERTISE STATUS FETCH ───────────────────────────────────────────
  // Read-only doctrine availability for the additive sixth tab and Strategy
  // pending guard. It never reads memory files in the browser.
  const fetchDoctrine = useCallback(async () => {
    setDoctrineLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/arthur/doctrine-status`);
      if (res.data?.success) {
        setDoctrine(res.data.doctrine as DoctrineStatus);
        setDoctrineErr(null);
      } else {
        setDoctrineErr(res.data?.error || 'doctrine status unavailable');
      }
    } catch (e) {
      console.error('[ARTHUR] Doctrine status fetch failed:', e);
      setDoctrineErr('doctrine status unavailable');
    } finally {
      setDoctrineLoading(false);
    }
  }, []);

  useEffect(() => { fetchDoctrine(); }, [fetchDoctrine]);
  useEffect(() => { if (tab === 'expertise') fetchDoctrine(); }, [tab, fetchDoctrine]);

  // Workbook download — the .xlsx route is Bearer-only, so a bare <a href> sends
  // no token and 401s. Fetch as a blob through axios (interceptor carries the
  // bearer), then hand the blob to a programmatic link.
  async function downloadLedgerXlsx() {
    setXlsxBusy(true);
    try {
      const res = await axios.get(`${API_BASE}/arthur/ledger.xlsx`, threadId ? { responseType: 'blob', params: { thread_id: threadId } } : { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'trades_2026.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[ARTHUR] Ledger export failed:', e);
    } finally {
      setXlsxBusy(false);
    }
  }

  // ── COMPUTED STATS ─────────────────────────────────────────────────────────
  const trades = selectedContext.mode === 'live'
    ? data.trades.filter(trade => trade.sessionId === selectedContext.sessionId) : data.trades;
  const selectedLive = data.selectedContext ? selectedContext.mode === 'live' : data.liveSessionActive;
  const discretionaryLive = selectedContext.mode === 'live' && selectedContext.approach === 'discretionary';
  const selectedAccountUnpriced = selectedContext.mode === 'live' && !!selectedContext.accountId;
  const closedTrades = homeClosedTradesFor(trades);
  const wins = closedTrades.filter(t => t.outcome === 'win');
  const losses = closedTrades.filter(t => t.outcome === 'loss');
  const winRate = closedTrades.length ? (wins.length / closedTrades.length * 100).toFixed(1) : '—';
  const avgWinRR = wins.length ? (wins.reduce((s, t) => s + (t.rr || 0), 0) / wins.length).toFixed(2) : '—';
  const avgLossRR = losses.length ? (losses.reduce((s, t) => s + (t.rr || 0), 0) / losses.length).toFixed(2) : '—';
  const totalRR = closedTrades.reduce((s, t) => s + (t.rr || 0), 0);
  const profitFactor = losses.length && wins.length
    ? (wins.reduce((s, t) => s + (t.rr || 0), 0) / Math.abs(losses.reduce((s, t) => s + (t.rr || 0), 0))).toFixed(2)
    : '—';
  const longs = closedTrades.filter(t => t.type === 'buy');
  const shorts = closedTrades.filter(t => t.type === 'sell');
  const longWins = longs.filter(t => t.outcome === 'win');
  const shortWins = shorts.filter(t => t.outcome === 'win');

  // Live Session carries today's activity and any still-open position across Hobart midnight.
  const liveSessionNow = new Date();
  const today = liveSessionNow.toLocaleDateString('en-AU', { timeZone: 'Australia/Hobart' });
  const todayTrades = liveSessionTradesFor(selectedContext.mode === 'live'
    ? trades.filter(trade => trade.sessionId === selectedContext.sessionId) : trades, liveSessionNow);
  const todayJournal = data.journal.filter(e => {
    if (selectedContext.mode === 'live' && e.sessionId !== selectedContext.sessionId) return false;
    const d = new Date(e.date).toLocaleDateString('en-AU', { timeZone: 'Australia/Hobart' });
    return d === today;
  });
  const todayScoredTrades = todayTrades.filter(isScored);
  const todayComparedTrades = todayScoredTrades.filter(isStrategyCompared);
  const todayRR = todayScoredTrades.reduce((s, t) => s + (t.rr as number), 0);
  const strategyDoctrineAvailable = doctrine?.strategy_substantive_status === 'OWNER_DOCTRINE_AVAILABLE';
  const strategyAdherenceValue = strategyDoctrineAvailable && todayComparedTrades.length
    ? `${(todayComparedTrades.filter(t => strategyDisplayStateFor(t) === 'aligned').length / todayComparedTrades.length * 100).toFixed(0)}%`
    : '—';

  // ── WRAPPER ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      // AppShell clips its children (`height:100vh; overflow:hidden`) and its content slot
      // declares no scroller, so a page taller than the viewport was simply cut off with no
      // way to reach the bottom. This page owns its own scroll region — same shape CodePage
      // uses. `height:100%` (not 100vh) so the domain bar's height is respected.
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: C.bg,
      color: C.text,
      fontFamily: 'var(--font-sans, system-ui)',
      padding: 0,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 24px',
        borderBottom: `0.5px solid ${C.border}`,
        background: C.card,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={onBack} disabled={sessionBusy || backtestSaving} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: C.textSub, fontSize: 18, padding: '2px 6px',
          borderRadius: 6, lineHeight: 1,
        }} aria-label="Back to Arthur chat">←</button>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Arthur — Trading Dashboard</div>
          <div style={{ fontSize: 10, color: C.textMuted, marginTop: 1 }}>
            Hobart · {new Date().toLocaleDateString('en-AU', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'Australia/Hobart' })}
          </div>
        </div>
        {selectedLive && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, display: 'inline-block', boxShadow: `0 0 6px ${C.green}` }} />
            <span style={{ fontSize: 11, color: C.green, fontWeight: 500 }}>Live Session Active</span>
          </div>
        )}
        <div style={{ marginLeft: selectedLive ? 16 : 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {resetConfirm ? (
            <>
              <span style={{ fontSize: 10, color: C.textMuted }}>Clear all trade data?</span>
              <button onClick={resetDashboard} disabled={resetting} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, border: `0.5px solid ${C.red}`, background: 'rgba(224,82,82,0.12)', color: C.red, cursor: 'pointer', fontFamily: 'inherit' }}>
                {resetting ? 'Resetting…' : 'Confirm Reset'}
              </button>
              <button onClick={() => setResetConfirm(false)} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={() => setResetConfirm(true)} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontFamily: 'inherit' }} title="Reset dashboard — clears all trade data. Arthur memory preserved.">
              ↺ Reset
            </button>
          )}
        </div>
      </div>

      {data.selectedContext && selectedContext.mode !== 'demo' && <section className="arthur-session-controls" aria-label="Arthur conversation context" style={{ ...card, margin: '12px 24px', color: C.text }}>
        <style>{`
          .arthur-session-controls button, .arthur-session-controls select {
            background: #141414; color: #ededed; border: 1px solid #2a2a2a;
            border-radius: 6px; padding: 6px 9px; font: inherit; font-size: 12px;
          }
          .arthur-session-controls button { cursor: pointer; }
          .arthur-session-controls button[aria-pressed="true"] { border-color: #BA7517; color: #f5c96b; }
          .arthur-session-controls button:disabled { opacity: .45; cursor: default; }
        `}</style>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong>{selectedContext.mode === 'live'
            ? `Live · ${selectedContext.instrument === 'XAUUSD' ? 'Gold' : 'Nasdaq'} · ${selectedContext.approach === 'main' ? 'Main Strategy' : 'Discretionary Trading'} · ${selectedContext.accountId || 'Account unassigned'}`
            : selectedContext.mode === 'backtesting' ? `Backtesting · ${selectedContext.backtestName || selectedContext.backtestId}` : 'General Conversation'}</strong>
          <button disabled={sessionBusy} aria-pressed={selectedContext.mode === 'live'}
            onClick={() => selectedContext.mode === 'live' ? sessionControl({ op: 'stop' }) : activateSession()}>
            LIVE SESSION — {selectedContext.mode === 'live' ? 'ON' : 'OFF'}
          </button>
          {selectedContext.mode === 'live' && <label>Discussion approach <select aria-label="Selected live approach"
            disabled={sessionBusy} value={selectedContext.approach}
            onChange={e => sessionControl({ op: 'activate', instrument: selectedContext.instrument,
              approach: e.target.value, accountId: selectedContext.accountId || null })}>
            <option value="main">Main Strategy</option><option value="discretionary">Discretionary Trading</option>
          </select></label>}
          <button disabled={sessionBusy || backtestSaving}
            aria-pressed={selectedContext.mode === 'backtesting'} onClick={() => {
              if (selectedContext.mode !== 'backtesting' && !selectedBacktest) {
                setTab('backtesting'); setShowNewBtInput(true);
              } else sessionControl(selectedContext.mode === 'backtesting'
                ? { op: 'general' } : { op: 'backtesting', backtestId: selectedBacktest });
            }}>
            BACKTESTING — {selectedContext.mode === 'backtesting' ? 'ON' : 'OFF'}
          </button>
          <label>Backtest <select aria-label="Conversation backtest" disabled={sessionBusy || backtestSaving}
            value={(selectedContext.mode === 'backtesting' ? selectedContext.backtestId : selectedBacktest) || ''}
            onChange={e => { if (selectedContext.mode === 'backtesting') {
              if (e.target.value) sessionControl({ op: 'backtesting', backtestId: e.target.value });
            } else setSelectedBacktest(e.target.value || null); }}>
            <option value="">Choose an existing backtest</option>{data.backtests.map(bt => <option key={bt.id} value={bt.id}>{bt.name}</option>)}
          </select></label>
          <button disabled={sessionBusy} onClick={() => sessionControl({ op: 'general' })}>General Conversation</button>
        </div>
        <div style={{ color: C.textSub, fontSize: 11, marginTop: 10 }}>Configure another session — applied only when you start it. Other live sessions stay active.</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          <label>New session instrument <select aria-label="Live instrument" value={sessionInstrument}
            onChange={e => selectLiveDetails({ instrument: e.target.value as 'XAUUSD' | 'NASDAQ' })}>
            <option value="XAUUSD">Gold</option><option value="NASDAQ">Nasdaq</option>
          </select></label>
          <label>New session approach <select aria-label="Live approach" value={sessionApproach}
            onChange={e => selectLiveDetails({ approach: e.target.value as 'main' | 'discretionary' })}>
            <option value="main">Main Strategy</option><option value="discretionary">Discretionary Trading</option>
          </select></label>
          <label>New session account <select aria-label="Live account" value={sessionAccount} onChange={e => selectLiveDetails({ accountId: e.target.value || null })}>
            <option value="">Unassigned</option>{(data.availableAccountIds || []).map(id => <option key={id} value={id}>{id}</option>)}
          </select></label>
          <button disabled={sessionBusy} onClick={() => sessionControl({ op: 'activate', newSession: true,
            instrument: sessionInstrument, approach: sessionApproach, accountId: sessionAccount || null })}>Start new live session</button>
          <label>Active session <select aria-label="Active live session" value={selectedContext.mode === 'live' ? selectedContext.sessionId : ''}
            disabled={sessionBusy} onChange={e => e.target.value && sessionControl({ op: 'select', sessionId: e.target.value })}>
            <option value="">Select a session</option>{activeSessions.map(session => <option key={session.id} value={session.id}>
              {session.instrument === 'XAUUSD' ? 'Gold' : 'Nasdaq'} · {session.approach === 'main' ? 'Main Strategy' : 'Discretionary'} · {session.accountId || 'Unassigned'} · {session.id.slice(-6)}
            </option>)}
          </select></label>
          <span>{activeSessions.length} live session{activeSessions.length === 1 ? '' : 's'} active</span>
        </div>
        {!data.availableAccountIds?.length && <p style={{ color: C.textSub, fontSize: 11 }}>No existing account identity is available. Sessions remain account-unassigned.</p>}
        {(sessionBusy || backtestSaving) && <p role="status" style={{ color: C.textSub, fontSize: 11 }}>Saving selection… The current context remains effective until saved.</p>}
        {sessionError && <p role="alert" style={{ color: C.red }}>{sessionError}</p>}
      </section>}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, padding: '0 24px',
        borderBottom: `0.5px solid ${C.border}`,
        background: C.card,
      }}>
        {([
          { key: 'home', label: 'Home' },
          { key: 'live', label: 'Live Session' },
          { key: 'backtesting', label: 'Backtesting' },
          { key: 'ledger', label: 'Ledger' },
          { key: 'dreams', label: 'Dreams & Journals' },
          { key: 'expertise', label: 'Deep Expertise' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabBtn(tab === t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Scroll region — the page's only scroller. Header and tab strip stay put above it;
          everything below scrolls to the full range of the page. Scrollbar chrome is already
          invisible OS-wide (index.css, owner ruling 2026-07-24), so this reads as an
          invisible scroller: it scrolls, nothing is drawn. */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}>

      {/* Content */}
      <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>

        {/* ── HOME TAB ──────────────────────────────────────────────────── */}
        {tab === 'home' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Top stat row — Account Size is editable */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textMuted }}>Account Size</div>
                {selectedAccountUnpriced ? <div style={{ fontSize: 12, color: C.textSub }}>No balance linked to {selectedContext.accountId}</div> :
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: C.text }}>$</span>
                  <input
                    type="number"
                    value={data.accountSize}
                    onChange={async e => {
                      const val = parseFloat(e.target.value) || 0;
                      setData(prev => ({ ...prev, accountSize: val }));
                      try {
                        await axios.post(`${API_BASE}/arthur/dashboard`, withThread({ accountSize: val }));
                      } catch {}
                    }}
                    style={{ fontSize: 20, fontWeight: 700, color: C.text, background: 'transparent', border: 'none', outline: 'none', fontFamily: 'inherit', width: '100%' }}
                  />
                </div>}
              </div>
              <StatTile label="Total RR" value={fmtRR(totalRR)} color={totalRR >= 0 ? C.green : C.red} />
              <StatTile label="Start Date" value={data.startDate} color={C.text} />
              <StatTile label="Total Trades" value={closedTrades.length.toString()} />
            </div>

            {/* Score + Balance row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <ScoreRadar trades={closedTrades} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Win rate stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, flex: 1 }}>
                  <StatTile label="Win Rate" value={`${winRate}%`} color={C.accent} />
                  <StatTile label="Profit Factor" value={profitFactor} color={C.green} />
                  <StatTile label="Avg Win RR" value={avgWinRR !== '—' ? `${avgWinRR}R` : '—'} color={C.green} />
                  <StatTile label="Avg Loss RR" value={avgLossRR !== '—' ? `${avgLossRR}R` : '—'} color={C.red} />
                </div>
              </div>
            </div>

            {/* Equity curve */}
            <div style={{ ...card }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Account Balance</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="date" value={dateRange.start}
                    onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
                    style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: '#1a1a1f', color: C.textSub, fontFamily: 'inherit' }} />
                  <input type="date" value={dateRange.end}
                    onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
                    style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: '#1a1a1f', color: C.textSub, fontFamily: 'inherit' }} />
                </div>
              </div>
              {selectedAccountUnpriced
                ? <EmptyState label="This portfolio account has no linked account size or balance" />
                : <EquityCurve trades={closedTrades} accountSize={data.accountSize} height={220} />}
            </div>

            {/* Long / Short analysis */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {/* Long */}
              <div style={{ ...card, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Long Analysis</div>
                <HalfDonut wins={longWins.length} losses={longs.length - longWins.length} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
                  <div><div style={{ color: C.green }}>Wins ({longWins.length})</div></div>
                  <div><div style={{ color: C.textSub }}>{longs.length ? (longWins.length / longs.length * 100).toFixed(1) : 0}%</div></div>
                  <div><div style={{ color: C.red }}>Losses ({longs.length - longWins.length})</div></div>
                </div>
              </div>
              {/* Short */}
              <div style={{ ...card, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Short Analysis</div>
                <HalfDonut wins={shortWins.length} losses={shorts.length - shortWins.length} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
                  <div><div style={{ color: C.green }}>Wins ({shortWins.length})</div></div>
                  <div><div style={{ color: C.textSub }}>{shorts.length ? (shortWins.length / shorts.length * 100).toFixed(1) : 0}%</div></div>
                  <div><div style={{ color: C.red }}>Losses ({shorts.length - shortWins.length})</div></div>
                </div>
              </div>
              {/* Profitability */}
              <div style={{ ...card, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Profitability</div>
                <HalfDonut wins={wins.length} losses={losses.length} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
                  <div><div style={{ color: C.green }}>{wins.length ? (wins.length / closedTrades.length * 100).toFixed(1) : 0}%</div><div style={{ color: C.textMuted }}>Wins: {wins.length}</div></div>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{closedTrades.length}</div>
                  <div><div style={{ color: C.red }}>{losses.length ? (losses.length / closedTrades.length * 100).toFixed(1) : 0}%</div><div style={{ color: C.textMuted }}>Losses: {losses.length}</div></div>
                </div>
              </div>
            </div>

            {/* Time of day grid */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Session Performance</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                  const amTrades = closedTrades.filter(t => t.session?.startsWith(`${day} AM`));
                  const pmTrades = closedTrades.filter(t => t.session?.startsWith(`${day} PM`));
                  const amRR = amTrades.reduce((s, t) => s + (t.rr || 0), 0);
                  const pmRR = pmTrades.reduce((s, t) => s + (t.rr || 0), 0);
                  return (
                    <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 9, color: C.textMuted, textAlign: 'center', fontWeight: 500 }}>{day.slice(0, 3)}</div>
                      <div style={{ ...card, padding: '8px 6px', textAlign: 'center', borderColor: amRR > 0 ? `rgba(29,158,117,0.3)` : amRR < 0 ? `rgba(224,82,82,0.3)` : C.cardBorder }}>
                        <div style={{ fontSize: 9, color: C.textMuted }}>AM</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: amRR > 0 ? C.green : amRR < 0 ? C.red : C.textMuted }}>
                          {amTrades.length ? fmtRR(amRR) : '—'}
                        </div>
                        <div style={{ fontSize: 9, color: C.textMuted }}>{amTrades.length}t</div>
                      </div>
                      <div style={{ ...card, padding: '8px 6px', textAlign: 'center', borderColor: pmRR > 0 ? `rgba(29,158,117,0.3)` : pmRR < 0 ? `rgba(224,82,82,0.3)` : C.cardBorder }}>
                        <div style={{ fontSize: 9, color: C.textMuted }}>PM</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: pmRR > 0 ? C.green : pmRR < 0 ? C.red : C.textMuted }}>
                          {pmTrades.length ? fmtRR(pmRR) : '—'}
                        </div>
                        <div style={{ fontSize: 9, color: C.textMuted }}>{pmTrades.length}t</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trading history */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Trading History</div>
              <TradeTable trades={closedTrades} onDelete={deleteTrade} />
            </div>

          </div>
        )}

        {/* ── LIVE SESSION TAB ───────────────────────────────────────────── */}
        {tab === 'live' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Session header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <StatTile label="Today's Trades" value={todayTrades.length.toString()} />
              <StatTile
                label="Today's RR"
                value={todayScoredTrades.length ? fmtRR(todayRR) : '—'}
                color={todayScoredTrades.length ? (todayRR >= 0 ? C.green : C.red) : C.textMuted}
              />
              <StatTile
                label={discretionaryLive ? 'Approach' : 'Strategy Adherence'}
                value={discretionaryLive ? 'Discretionary' : strategyAdherenceValue}
                color={discretionaryLive || strategyDoctrineAvailable ? C.accent : C.textMuted}
                sub={discretionaryLive ? 'Owner-led' : strategyDoctrineAvailable ? undefined : 'Strategy pending'}
              />
              <StatTile
                label="Session"
                value={selectedLive ? 'Active' : 'Closed'}
                color={selectedLive ? C.green : C.textMuted}
                sub="Hobart time"
              />
            </div>

            {/* Strategy vs Human equity */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{discretionaryLive ? 'Discretionary Trading' : 'Strategy vs Execution'}</div>
              {discretionaryLive
                ? <div style={{ fontSize: 12, color: C.textSub }}>Owner-led analysis and decisions. No formal discretionary strategy or indicator is configured. Arthur discusses; the owner executes.</div>
                : !strategyDoctrineAvailable
                ? <EmptyState label="Strategy doctrine has not been installed" />
                : <>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>Strategy installed. Each trade is assessed only from complete linked setup evidence.</div>
                    {todayTrades.some(t => t.strategy_comparison?.dimensions?.length)
                      ? todayTrades.filter(t => t.strategy_comparison?.dimensions?.length).map(t => <div key={t.id}><strong>{t.symbol}</strong><StrategyComparisonDetails comparison={t.strategy_comparison} /></div>)
                      : <EmptyState label="Unassessed — complete setup evidence has not been established for these trades" />}
                  </>}

            </div>

            {/* Today's trades */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Today's Trades</div>
              <TradeTable trades={todayTrades} onDelete={deleteTrade} showLiveStateColumns doctrineInstalled={strategyDoctrineAvailable} />
            </div>

            {/* Arthur's Journal */}
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Arthur's Journal</div>
                <div style={{ fontSize: 10, color: C.textMuted }}>Updated live during session</div>
              </div>
              <Journal entries={todayJournal} />
            </div>

          </div>
        )}

        {/* ── BACKTESTING TAB ────────────────────────────────────────────── */}
        {tab === 'backtesting' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Backtest tab bar — Overview + one tab per named backtest + New button */}
            <div style={{
              display: 'flex', gap: 0, alignItems: 'center',
              borderBottom: `0.5px solid ${C.border}`,
              marginBottom: 16, overflowX: 'auto',
            }}>
              <button
                onClick={() => setSelectedBacktest(null)}
                style={{ fontSize: 12, padding: '5px 16px', borderRadius: '6px 6px 0 0',
                  border: `0.5px solid ${selectedBacktest === null ? C.border : 'transparent'}`,
                  cursor: 'pointer', background: selectedBacktest === null ? '#1e1e24' : 'transparent',
                  color: selectedBacktest === null ? C.text : C.textSub,
                  fontWeight: selectedBacktest === null ? 500 : 400, fontFamily: 'inherit',
                  whiteSpace: 'nowrap', transition: 'all 0.15s',
                }}
              >
                Overview
              </button>

              {data.backtests.map((bt, i) => {
                const btColors = [C.chart1, C.chart2, C.chart3, C.chart4];
                const isActive = selectedBacktest === bt.id;
                return (
                  <button key={bt.id} disabled={sessionBusy || backtestSaving} onClick={() => {
                    if (selectedContext.mode === 'backtesting') sessionControl({ op: 'backtesting', backtestId: bt.id });
                    else setSelectedBacktest(bt.id);
                  }}
                    style={{ fontSize: 12, padding: '5px 14px', borderRadius: '6px 6px 0 0',
                      border: `0.5px solid ${isActive ? C.border : 'transparent'}`,
                      cursor: 'pointer', background: isActive ? '#1e1e24' : 'transparent',
                      color: isActive ? C.text : C.textSub, fontWeight: isActive ? 500 : 400,
                      fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%',
                      background: btColors[i % btColors.length], display: 'inline-block', flexShrink: 0 }} />
                    {bt.name}
                    <span style={{ fontSize: 9, color: C.textMuted }}>({bt.trades.length})</span>
                  </button>
                );
              })}

              {/* Add new backtest — inline input, no prompt() */}
              {showNewBtInput ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px' }}>
                  <input
                    autoFocus
                    disabled={backtestSaving}
                    value={newBtName}
                    onChange={e => setNewBtName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') createBacktest();
                      if (e.key === 'Escape') {
                        setShowNewBtInput(false);
                        setNewBtName('');
                      }
                    }}
                    placeholder="Name this backtest... (Enter to confirm)"
                    style={{
                      fontSize: 11, padding: '4px 10px', borderRadius: 6,
                      border: `0.5px solid ${C.accent}`,
                      background: '#1a1a28', color: C.text,
                      fontFamily: 'inherit', outline: 'none', width: 260,
                    }}
                  />
                  <button
                    onClick={() => { setShowNewBtInput(false); setNewBtName(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 14, padding: '2px 4px' }}
                  >✕</button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewBtInput(true)}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: '6px 6px 0 0',
                    border: 'none', cursor: 'pointer', background: 'transparent',
                    color: C.textMuted, fontFamily: 'inherit', marginLeft: 4,
                    transition: 'color 0.15s',
                  }}
                  title="Create new named backtest"
                >
                  + New
                </button>
              )}
            </div>

            {/* ── OVERVIEW — comparison chart + summary table ── */}
            {selectedBacktest === null && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {(() => {
                    // ── Aggregate roll-up across every backtest tab, computed live ──
                    // This layout IS the page — it is the RESTING STATE, not a populated
                    // alternative to a placeholder. At zero backtests it renders the same
                    // structure with honest zeros and em-dashes, then fills as setups land.
                    const rows = overviewRows(data.backtests);
                    const totals = overviewTotals(data.backtests, rows);
                    const byGrade = overviewByGrade(rows);
                    const ctx = overviewContext(data.backtests, rows);
                    const maxGradeN = Math.max(...byGrade.map(g => g.n), 1);

                    // Filter applies to the setup library only — the cards above stay whole-set.
                    const library = rows
                      .filter(r => matchesOverviewQuery(r, overviewQuery))
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                    const th: React.CSSProperties = {
                      textAlign: 'left', padding: '6px 10px', color: C.textMuted,
                      fontWeight: 500, borderBottom: `0.5px solid ${C.border}`, whiteSpace: 'nowrap',
                    };
                    const td: React.CSSProperties = { padding: '8px 10px', color: C.textSub };

                    return (
                      <>
                        {/* ── Context strip ── */}
                        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', padding: '10px 16px' }}>
                          {([
                            { label: 'Instrument', value: ctx.instrument },
                            { label: 'Strategy', value: ctx.strategy },
                            { label: 'Replay period', value: ctx.period },
                          ] as const).map(f => (
                            <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{f.label}</span>
                              <span style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{f.value}</span>
                            </div>
                          ))}
                          <span
                            style={{ marginLeft: 'auto', fontSize: 11, color: C.textMuted, cursor: 'default' }}
                            title="Create a replay session from the + New control on the backtest tab bar above"
                          >
                            ▸ New replay session
                          </span>
                        </div>

                        {/* ── Four aggregate cards ── */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                          <StatTile
                            label="Backtests Run"
                            value={totals.backtestsRun.toString()}
                          />
                          <StatTile
                            label="Setups Scored"
                            value={totals.setupsScored.toString()}
                            sub={totals.unscored ? `${totals.unscored} unscored` : undefined}
                          />
                          <StatTile
                            label="Win Rate"
                            value={totals.winRate === null ? '—' : `${totals.winRate.toFixed(1)}%`}
                            color={C.accent}
                            sub={totals.setupsScored ? `${totals.wins} of ${totals.setupsScored} scored` : undefined}
                          />
                          <StatTile
                            label="Avg RR"
                            value={totals.avgRR === null ? '—' : fmtRR(totals.avgRR)}
                            color={totals.avgRR === null ? C.text : totals.avgRR >= 0 ? C.green : C.red}
                          />
                        </div>

                        {/* ── Setup quality distribution ── */}
                        <div style={card}>
                          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Setup Quality</div>
                          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
                            Grade distribution across all scored setups — strategy adherence × RR band
                          </div>
                          {/* Bars are ALWAYS present — at rest every grade sits at 0 rather
                              than the whole distribution being swapped for a placeholder. */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {byGrade.map(g => (
                              <div key={g.grade} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: g.color, width: 22, flexShrink: 0 }}>{g.grade}</span>
                                <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.04)', overflow: 'hidden' }}>
                                  <div style={{
                                    // maxGradeN floors at 1, so zero setups → 0% width, never NaN%
                                    width: `${(g.n / maxGradeN) * 100}%`, height: '100%',
                                    background: g.color, borderRadius: 4, transition: 'width 0.2s',
                                  }} />
                                </div>
                                <span style={{ fontSize: 11, color: g.n ? C.textSub : C.textMuted, width: 46, textAlign: 'right', flexShrink: 0 }}>
                                  {g.n}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ── Stats by grade ── */}
                        <div style={card}>
                          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Stats by Grade</div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr>
                                  {['Grade', 'Win %', 'Avg R', 'N'].map(h => (
                                    <th key={h} style={th}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {byGrade.map(g => (
                                  <tr key={g.grade} style={{ borderBottom: `0.5px solid ${C.cardBorder2}` }}>
                                    <td style={{ padding: '8px 10px', color: g.color, fontWeight: 600 }}>{g.grade}</td>
                                    <td style={{ padding: '8px 10px', color: g.winPct === null ? C.textMuted : C.accent }}>
                                      {g.winPct === null ? '—' : `${g.winPct.toFixed(1)}%`}
                                    </td>
                                    <td style={{ padding: '8px 10px', fontWeight: 600,
                                      color: g.avgR === null ? C.textMuted : g.avgR >= 0 ? C.green : C.red }}>
                                      {g.avgR === null ? '—' : fmtRR(g.avgR)}
                                    </td>
                                    <td style={td}>{g.n}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* ── Setup library + filter ── */}
                        <div style={card}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>Setup Library</div>
                            <input
                              value={overviewQuery}
                              onChange={e => setOverviewQuery(e.target.value)}
                              placeholder="Filter — grade, instrument, strategy, session, outcome, date…"
                              style={{
                                marginLeft: 'auto', flex: '1 1 260px', maxWidth: 380,
                                fontSize: 11, padding: '4px 10px', borderRadius: 6,
                                border: `0.5px solid ${C.border}`, background: '#1a1a1f',
                                color: C.text, fontFamily: 'inherit', outline: 'none',
                              }}
                            />
                            <span style={{ fontSize: 10, color: C.textMuted, whiteSpace: 'nowrap' }}>
                              {library.length} of {rows.length}
                            </span>
                            {overviewQuery && (
                              <button
                                onClick={() => setOverviewQuery('')}
                                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6,
                                  border: `0.5px solid ${C.border}`, background: 'transparent',
                                  color: C.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          {/* The table — headers and all — is ALWAYS rendered. An empty
                              library speaks through one quiet in-body row, not a swapped view. */}
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr>
                                  {['Date', 'Instr', 'Strategy', 'Setup', 'Grade', 'Out', 'R'].map(h => (
                                    <th key={h} style={th}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                  {library.length === 0 && (
                                    <tr>
                                      <td colSpan={7} style={{ padding: '20px 10px', textAlign: 'center', color: C.textMuted, fontSize: 11 }}>
                                        {rows.length === 0
                                          ? 'No setups logged yet'
                                          : `No setups match “${overviewQuery.trim()}”`}
                                      </td>
                                    </tr>
                                  )}
                                  {library.map((r, i) => (
                                    <tr key={r.trade.id || `${r.backtestId}-${i}`}
                                      style={{ borderBottom: `0.5px solid ${C.cardBorder2}`,
                                        background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        {r.date
                                          ? new Date(r.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
                                          : '—'}
                                      </td>
                                      <td style={{ padding: '8px 10px', color: C.text, fontWeight: 500 }}>{r.trade.symbol || '—'}</td>
                                      <td style={td}>{r.backtestName}</td>
                                      <td style={{ ...td, color: C.textSub, maxWidth: 280 }}>
                                        {r.trade.notes || r.trade.session || '—'}
                                      </td>
                                      <td style={{ padding: '8px 10px' }}>
                                        {r.grade ? (
                                          <span style={{ color: GRADE_COLOR[r.grade], fontWeight: 600 }}>{r.grade}</span>
                                        ) : (
                                          <span style={{ color: C.textMuted }} title="Unscored — excluded from grades, win rate and average R">—</span>
                                        )}
                                      </td>
                                      <td style={{ padding: '8px 10px',
                                        color: r.trade.outcome === 'win' ? C.green
                                          : r.trade.outcome === 'loss' ? C.red : C.textMuted }}>
                                        {r.trade.outcome ? r.trade.outcome.toUpperCase() : '—'}
                                      </td>
                                      <td style={{ padding: '8px 10px', fontWeight: 600,
                                        color: !r.scored ? C.textMuted : (r.trade.rr as number) >= 0 ? C.green : C.red }}>
                                        {r.scored ? fmtRR(r.trade.rr as number) : '—'}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* ── Footer — live, not nightly ── */}
                        <div style={{ fontSize: 10, color: C.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
                          Rolled up live from every backtest tab — recomputed as you log setups.
                          <br />
                          Grades read strategy adherence against the RR band. Unscored setups are excluded from grades, win rate and average R.
                        </div>
                      </>
                    );
                })()}
              </div>
            )}

            {/* ── INDIVIDUAL BACKTEST — full Home-style dashboard, no strategy match ── */}
            {selectedBacktest && data.selectedContext && <div style={{ marginBottom: 12, color: C.textSub, fontSize: 11 }}>
              <button disabled={sessionBusy || backtestSaving} onClick={() => sessionControl({ op: 'backtesting', backtestId: selectedBacktest })}
                style={{ background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px' }}>Run</button>
              <span style={{ marginLeft: 10 }}>Discuss this saved test with Arthur. Trades remain hypothetical.</span>
            </div>}
            {selectedBacktest !== null && (() => {
              const bt = data.backtests.find(b => b.id === selectedBacktest);
              if (!bt) return null;
              const btClosed = bt.trades.filter(isScored);
              const btW = btClosed.filter(t => t.outcome === 'win');
              const btL = btClosed.filter(t => t.outcome === 'loss');
              const btLongs = btClosed.filter(t => t.type === 'buy');
              const btShorts = btClosed.filter(t => t.type === 'sell');
              const btLongW = btLongs.filter(t => t.outcome === 'win');
              const btShortW = btShorts.filter(t => t.outcome === 'win');
              const btRR = btClosed.reduce((s, t) => s + (t.rr || 0), 0);
              const btWR = btClosed.length ? (btW.length / btClosed.length * 100).toFixed(1) : '—';
              const btAvgW = btW.length ? (btW.reduce((s, t) => s + (t.rr || 0), 0) / btW.length).toFixed(2) : '—';
              const btAvgL = btL.length ? (btL.reduce((s, t) => s + (t.rr || 0), 0) / btL.length).toFixed(2) : '—';
              const btPF = btL.length && btW.length
                ? (btW.reduce((s, t) => s + (t.rr || 0), 0) / Math.abs(btL.reduce((s, t) => s + (t.rr || 0), 0))).toFixed(2)
                : '—';

              // Backtest trade table — no strategy match column
              const BtTradeTable = ({ trades, onDelete }: { trades: Trade[]; onDelete?: (id: string) => void }) => {
                if (trades.length === 0) return <EmptyState label="No trades logged yet" />;
                const sorted = [...trades].sort((a, b) =>
                  new Date(b.openDate).getTime() - new Date(a.openDate).getTime());
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {[...['Symbol','Type','Session','Open','Close','Open Price','Close Price','RR','Risk %'], ...(onDelete ? [''] : [])].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.textMuted,
                              fontWeight: 500, borderBottom: `0.5px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((t, i) => (
                          <tr key={t.id} style={{ borderBottom: `0.5px solid ${C.cardBorder2}`,
                            background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                            <td style={{ padding: '8px 10px', color: C.text, fontWeight: 500 }}>{t.symbol}</td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={pill(t.type === 'buy' ? C.greenDark : C.redDark,
                                t.type === 'buy' ? C.greenLight : C.redLight)}>
                                {t.type.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', color: C.textSub }}>{t.session}</td>
                            <td style={{ padding: '8px 10px', color: C.textSub }}>
                              {new Date(t.openDate).toLocaleString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={{ padding: '8px 10px', color: C.textSub }}>
                              {t.closeDate ? new Date(t.closeDate).toLocaleString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td style={{ padding: '8px 10px', color: C.text }}>{t.openPrice}</td>
                            <td style={{ padding: '8px 10px', color: C.text }}>{t.closePrice || '—'}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600, color: rrColor(t.rr) }}>
                              {fmtRR(t.rr)}
                            </td>
                            <td style={{ padding: '8px 10px', color: C.textSub }}>
                              {(t as any).riskPct ? `${(t as any).riskPct}%` : '—'}
                            </td>
                            {onDelete && (
                              <td style={{ padding: '8px 10px' }}>
                                <button onClick={() => onDelete(t.id)} title="Delete trade" style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: `0.5px solid ${C.redDark}`, background: 'transparent', color: C.red, cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              };

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                  {/* Backtest header */}
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{bt.name}</div>
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                      Created {bt.createdAt} · {btClosed.length} trades · Pure strategy environment
                    </div>
                  </div>

                  {/* Top stats — no strategy match */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textMuted }}>Account Size</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>$</span>
                        <input
                          type="number"
                          value={bt.accountSize ?? data.accountSize}
                          onChange={async e => {
                            const val = parseFloat(e.target.value) || 0;
                            // Update per-backtest account size only — never touches live/home
                            setData(prev => ({
                              ...prev,
                              backtests: prev.backtests.map(b =>
                                b.id === bt.id ? { ...b, accountSize: val } : b
                              ),
                            }));
                            try {
                              await axios.patch(`${API_BASE}/arthur/backtest/${bt.id}/notes`, withThread({ accountSize: val }));
                            } catch {}
                          }}
                          style={{ fontSize: 16, fontWeight: 700, color: C.text, background: 'transparent', border: 'none', outline: 'none', fontFamily: 'inherit', width: '100%' }}
                        />
                      </div>
                    </div>
                    <StatTile label="Total Trades" value={btClosed.length.toString()} />
                    <StatTile label="Total RR" value={fmtRR(btRR)} color={btRR >= 0 ? C.green : C.red} />
                    <StatTile label="Win Rate" value={`${btWR}%`} color={C.accent} />
                    <StatTile label="Profit Factor" value={btPF} color={C.green} />
                  </div>

                  {/* Score radar + secondary stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <ScoreRadar trades={btClosed} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <StatTile label="Avg Win RR" value={btAvgW !== '—' ? `${btAvgW}R` : '—'} color={C.green} />
                      <StatTile label="Avg Loss RR" value={btAvgL !== '—' ? `${btAvgL}R` : '—'} color={C.red} />
                      <StatTile label="Long Trades" value={btLongs.length.toString()} />
                      <StatTile label="Short Trades" value={btShorts.length.toString()} />
                    </div>
                  </div>

                  {/* Equity curve */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Equity Curve</div>
                    <EquityCurve trades={btClosed} accountSize={bt.accountSize ?? data.accountSize} height={220} />
                  </div>

                  {/* Equity curve comparison — this backtest read against the others */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Equity Curve Comparison</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
                      All backtests overlaid — trade # on X axis · <span style={{ color: C.text }}>{bt.name}</span> highlighted
                    </div>
                    {(() => {
                      const btColors = [C.chart1, C.chart2, C.chart3, C.chart4];
                      // Same scored predicate and same P&L basis as the Equity Curve above it,
                      // so the two charts in this tab can never disagree.
                      const scoredOf = (b: BacktestData) => b.trades.filter(isScored);
                      const maxLen = Math.max(0, ...data.backtests.map(b => scoredOf(b).length));
                      if (maxLen === 0) return <EmptyState label="Log trades to see equity curves" />;
                      const pts: any[] = Array.from({ length: maxLen }, (_, i) => ({ trade: i + 1 }));
                      data.backtests.forEach(b => {
                        const acct = b.accountSize ?? data.accountSize;
                        let bal = acct;
                        scoredOf(b).forEach((t, ti) => {
                          const riskPct = t.riskPct != null && t.riskPct > 0 ? t.riskPct : 0.5;
                          bal += (t.rr as number) * (acct * (riskPct / 100));
                          pts[ti][b.name] = parseFloat(bal.toFixed(2));
                        });
                      });
                      return (
                        <ResponsiveContainer width="100%" height={260}>
                          <LineChart data={pts} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.cardBorder} />
                            <XAxis dataKey="trade" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} width={64}
                              tickFormatter={(v: number) => `$${(v/1000).toFixed(1)}k`} />
                            <Tooltip content={<ChartTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {data.backtests.map((b, i) => {
                              const isThis = b.id === bt.id;
                              return (
                                <Line key={b.id} type="monotone" dataKey={b.name}
                                  stroke={btColors[i % btColors.length]}
                                  strokeWidth={isThis ? 2.4 : 1.2}
                                  strokeOpacity={isThis ? 1 : 0.4}
                                  dot={false} connectNulls />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </div>

                  {/* Long / Short / Profitability donuts */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div style={{ ...card, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Long Analysis</div>
                      <HalfDonut wins={btLongW.length} losses={btLongs.length - btLongW.length} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
                        <div style={{ color: C.green }}>Wins ({btLongW.length})</div>
                        <div style={{ color: C.textSub }}>{btLongs.length ? (btLongW.length / btLongs.length * 100).toFixed(1) : 0}%</div>
                        <div style={{ color: C.red }}>Losses ({btLongs.length - btLongW.length})</div>
                      </div>
                    </div>
                    <div style={{ ...card, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Short Analysis</div>
                      <HalfDonut wins={btShortW.length} losses={btShorts.length - btShortW.length} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
                        <div style={{ color: C.green }}>Wins ({btShortW.length})</div>
                        <div style={{ color: C.textSub }}>{btShorts.length ? (btShortW.length / btShorts.length * 100).toFixed(1) : 0}%</div>
                        <div style={{ color: C.red }}>Losses ({btShorts.length - btShortW.length})</div>
                      </div>
                    </div>
                    <div style={{ ...card, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Profitability</div>
                      <HalfDonut wins={btW.length} losses={btL.length} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
                        <div>
                          <div style={{ color: C.green }}>{btClosed.length ? (btW.length / btClosed.length * 100).toFixed(1) : 0}%</div>
                          <div style={{ color: C.textMuted }}>Wins: {btW.length}</div>
                        </div>
                        <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{btClosed.length}</div>
                        <div>
                          <div style={{ color: C.red }}>{btClosed.length ? (btL.length / btClosed.length * 100).toFixed(1) : 0}%</div>
                          <div style={{ color: C.textMuted }}>Losses: {btL.length}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Session performance grid */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Session Performance</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                        const amT = btClosed.filter(t => t.session?.startsWith(`${day} AM`));
                        const pmT = btClosed.filter(t => t.session?.startsWith(`${day} PM`));
                        const amRR = amT.reduce((s, t) => s + (t.rr || 0), 0);
                        const pmRR = pmT.reduce((s, t) => s + (t.rr || 0), 0);
                        return (
                          <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ fontSize: 9, color: C.textMuted, textAlign: 'center', fontWeight: 500 }}>{day.slice(0,3)}</div>
                            <div style={{ ...card, padding: '8px 6px', textAlign: 'center',
                              borderColor: amRR > 0 ? 'rgba(29,158,117,0.3)' : amRR < 0 ? 'rgba(224,82,82,0.3)' : C.cardBorder }}>
                              <div style={{ fontSize: 9, color: C.textMuted }}>AM</div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: amRR > 0 ? C.green : amRR < 0 ? C.red : C.textMuted }}>
                                {amT.length ? fmtRR(amRR) : '—'}
                              </div>
                              <div style={{ fontSize: 9, color: C.textMuted }}>{amT.length}t</div>
                            </div>
                            <div style={{ ...card, padding: '8px 6px', textAlign: 'center',
                              borderColor: pmRR > 0 ? 'rgba(29,158,117,0.3)' : pmRR < 0 ? 'rgba(224,82,82,0.3)' : C.cardBorder }}>
                              <div style={{ fontSize: 9, color: C.textMuted }}>PM</div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: pmRR > 0 ? C.green : pmRR < 0 ? C.red : C.textMuted }}>
                                {pmT.length ? fmtRR(pmRR) : '—'}
                              </div>
                              <div style={{ fontSize: 9, color: C.textMuted }}>{pmT.length}t</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trade history — pure strategy, no strategy match column */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Trade History</div>

                    {/* ── TRADE ENTRY FORM ─────────────────────────────── */}
                    {(() => {
                      const inp = (extra: React.CSSProperties = {}) => ({
                        fontSize: 12, padding: '5px 8px', borderRadius: 6,
                        border: `0.5px solid ${C.border}`, background: '#1a1a24',
                        color: C.text, fontFamily: 'inherit', outline: 'none', ...extra,
                      } as React.CSSProperties);
                      return (
                        <div style={{ marginBottom: 12 }}>
                          {!showTradeForm ? (
                            <button onClick={() => setShowTradeForm(true)} style={{
                              fontSize: 11, padding: '5px 14px', borderRadius: 6,
                              border: `0.5px solid ${C.accent}`, background: 'rgba(127,119,221,0.1)',
                              color: C.accent, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 12,
                            }}>+ Log Trade</button>
                          ) : (
                            <div style={{ background: '#13131a', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.accent, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Log Trade — {bt.name}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>Symbol</div>
                                  <input value={tradeForm.symbol} onChange={e => setTradeForm(f => ({ ...f, symbol: e.target.value }))} placeholder="e.g. XAUUSD" style={inp({ width: '100%' })} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>Direction</div>
                                  <select value={tradeForm.type} onChange={e => setTradeForm(f => ({ ...f, type: e.target.value as 'buy'|'sell' }))} style={inp({ width: '100%' })}>
                                    <option value="buy">Buy / Long</option>
                                    <option value="sell">Sell / Short</option>
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>Outcome</div>
                                  <select value={tradeForm.outcome} onChange={e => setTradeForm(f => ({ ...f, outcome: e.target.value as 'win'|'loss'|'be' }))} style={inp({ width: '100%' })}>
                                    <option value="win">Win</option>
                                    <option value="loss">Loss</option>
                                    <option value="be">Breakeven</option>
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>RR (e.g. 3)</div>
                                  <input value={tradeForm.rr} onChange={e => setTradeForm(f => ({ ...f, rr: e.target.value }))} placeholder="3" type="number" step="0.1" style={inp({ width: '100%' })} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>Risk %</div>
                                  <input value={tradeForm.riskPct} onChange={e => setTradeForm(f => ({ ...f, riskPct: e.target.value }))} placeholder="0.5" type="number" step="0.1" style={inp({ width: '100%' })} />
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>Session</div>
                                  <select value={tradeForm.session} onChange={e => setTradeForm(f => ({ ...f, session: e.target.value }))} style={inp({ width: '100%' })}>
                                    {SESSIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 3 }}>Notes (optional)</div>
                                  <input value={tradeForm.notes} onChange={e => setTradeForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any observations..." style={inp({ width: '100%' })} />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={() => submitTrade(bt.id, bt.name)} disabled={tradeSaving || !tradeForm.symbol.trim() || !tradeForm.rr}
                                  style={{ fontSize: 11, padding: '5px 16px', borderRadius: 6, border: 'none', background: C.accent, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', opacity: (!tradeForm.symbol.trim() || !tradeForm.rr) ? 0.5 : 1 }}>
                                  {tradeSaving ? 'Saving…' : 'Confirm Trade'}
                                </button>
                                <button onClick={() => setShowTradeForm(false)}
                                  style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <BtTradeTable trades={bt.trades} onDelete={deleteTrade} />
                  </div>

                  {/* Backtest summary — every backtest side by side; rows stay click-through
                      so this doubles as the jump between tabs. Current tab is marked. */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Backtest Summary</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
                      Click a row to open that backtest · figures over scored setups only
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr>
                            {['Backtest', 'Scored', 'Win Rate', 'Total RR', 'Avg RR', 'Profit Factor', 'Created'].map(h => (
                              <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.textMuted,
                                fontWeight: 500, borderBottom: `0.5px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.backtests.map((b, i) => {
                            const btColors = [C.chart1, C.chart2, C.chart3, C.chart4];
                            const isThis = b.id === bt.id;
                            // Scored-only, same predicate as everywhere else — an unscored row
                            // must not sit in the win-rate denominator.
                            const sc = b.trades.filter(isScored);
                            const w = sc.filter(t => t.outcome === 'win');
                            const l = sc.filter(t => t.outcome === 'loss');
                            const sumRR = sc.reduce((s, t) => s + (t.rr as number), 0);
                            const sumW = w.reduce((s, t) => s + (t.rr as number), 0);
                            const sumL = l.reduce((s, t) => s + (t.rr as number), 0);
                            const pf = l.length && w.length && sumL !== 0
                              ? (sumW / Math.abs(sumL)).toFixed(2)
                              : '—';
                            return (
                              <tr key={b.id} onClick={() => setSelectedBacktest(b.id)}
                                title={isThis ? 'Currently open' : `Open ${b.name}`}
                                style={{ borderBottom: `0.5px solid ${C.cardBorder2}`, cursor: 'pointer',
                                  background: isThis ? 'rgba(127,119,221,0.07)' : 'transparent' }}>
                                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                  <span style={{ color: btColors[i % btColors.length], fontWeight: 600 }}>● </span>
                                  <span style={{ color: C.text, fontWeight: isThis ? 600 : 400 }}>{b.name}</span>
                                  {isThis && <span style={{ fontSize: 9, color: C.accent, marginLeft: 6 }}>current</span>}
                                </td>
                                <td style={{ padding: '8px 10px', color: C.textSub }}>{sc.length}</td>
                                <td style={{ padding: '8px 10px', color: sc.length ? C.accent : C.textMuted }}>
                                  {sc.length ? `${(w.length / sc.length * 100).toFixed(1)}%` : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', fontWeight: 600,
                                  color: !sc.length ? C.textMuted : sumRR >= 0 ? C.green : C.red }}>
                                  {sc.length ? fmtRR(sumRR) : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', color: C.textSub }}>
                                  {sc.length ? fmtRR(sumRR / sc.length) : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', color: pf === '—' ? C.textMuted : C.green }}>{pf}</td>
                                <td style={{ padding: '8px 10px', color: C.textMuted }}>{b.createdAt}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Arthur's analysis */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Arthur's Analysis</div>
                    {bt.notes
                      ? <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>{bt.notes}</div>
                      : <EmptyState label="Arthur's backtest analysis will appear here as sessions accumulate" />
                    }
                  </div>

                </div>
              );
            })()}

          </div>
        )}

        {/* ── LEDGER TAB — read-only per-trade log, stacked by date ──────── */}
        {tab === 'ledger' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(() => {
              // Mirror-first: dashboard store → mirror → UI. Client compute over
              // data.trades remains ONLY as the graceful fallback while the mirror
              // is loading or unavailable.
              const usingMirror = !!(ledgerMirror && Array.isArray(ledgerMirror.rows));
              const allRows = usingMirror
                ? ledgerRowsFromMirror(selectedContext.mode === 'live'
                    ? ledgerMirror.rows.filter(row => row.session_id === selectedContext.sessionId) : ledgerMirror.rows)
                : ledgerRows(trades);

              const now = Date.now();
              const cutoff =
                ledgerPeriod === '7d'  ? now - 7 * 86400000 :
                ledgerPeriod === '30d' ? now - 30 * 86400000 :
                ledgerPeriod === 'ytd' ? new Date(new Date().getFullYear(), 0, 1).getTime() :
                null;
              const rows = cutoff === null ? allRows : allRows.filter(r => r.ts >= cutoff);
              // Cards render the mirror's PRECOMPUTED rollups whenever they cover the
              // selected scope (whole set = 'all'); a narrower period re-scopes over
              // mirror rows client-side — same math, same scored predicate. `wins`
              // (sub-line only) isn't served, so it's counted from the same rows.
              const clientTotals = ledgerTotals(rows);
              const totals = usingMirror && ledgerPeriod === 'all' && selectedContext.mode !== 'live'
                ? {
                    trades: ledgerMirror!.rollups.trades,
                    netR: ledgerMirror!.rollups.net_r,
                    winRate: ledgerMirror!.rollups.win_rate,
                    avgR: ledgerMirror!.rollups.avg_r,
                    wins: clientTotals.wins,
                    scored: ledgerMirror!.rollups.scored,
                    unscored: ledgerMirror!.rollups.unscored,
                  }
                : clientTotals;

              // "Ask this log" scopes the table only — the period cards stay whole-period.
              const log = rows.filter(r => matchesLedgerQuery(r, ledgerQuery));

              // Stack by Hobart-local day, newest day first (rows are already ts-desc).
              const groups: { key: string; ts: number; rows: LedgerRow[] }[] = [];
              log.forEach(r => {
                const g = groups[groups.length - 1];
                if (g && g.key === r.dayKey) g.rows.push(r);
                else groups.push({ key: r.dayKey, ts: r.ts, rows: [r] });
              });

              const th: React.CSSProperties = {
                textAlign: 'left', padding: '6px 10px', color: C.textMuted,
                fontWeight: 500, borderBottom: `0.5px solid ${C.border}`, whiteSpace: 'nowrap',
              };
              const fmtTime = (ts: number) => ts
                ? new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Hobart' })
                : '—';
              const fmtDay = (ts: number) => ts
                ? new Date(ts).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Australia/Hobart' })
                : 'Undated';
              const fmtDateShort = (ts: number) => ts
                ? new Date(ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Australia/Hobart' })
                : '—';

              const periods = [
                { key: '7d', label: '7D' }, { key: '30d', label: '30D' },
                { key: 'ytd', label: 'YTD' }, { key: 'all', label: 'All' },
              ] as const;

              return (
                <>
                  {/* ── Sync status strip — live from the mirror's generated_at ── */}
                  <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 16px' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
                      background: usingMirror ? C.green : ledgerErr ? C.amber : C.textMuted,
                      boxShadow: usingMirror ? `0 0 6px ${C.green}` : 'none' }} />
                    {(() => {
                      if (usingMirror) {
                        const gen = Date.parse(ledgerMirror!.generated_at);
                        const t = Number.isFinite(gen)
                          ? new Date(gen).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Hobart' })
                          : '—';
                        const d = Number.isFinite(gen)
                          ? new Date(gen).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Hobart' })
                          : '—';
                        return (
                          <span style={{ fontSize: 11, color: C.textSub }} title="Materialised nightly at 00:02, and lazily on first read">
                            Last sync · {t} · {d} · {ledgerMirror!.rollups.trades} trades mirrored · <span style={{ color: C.text }}>trades_2026.xlsx</span>
                          </span>
                        );
                      }
                      if (ledgerLoading) {
                        return <span style={{ fontSize: 11, color: C.textSub }}>Syncing mirror…</span>;
                      }
                      return (
                        <span style={{ fontSize: 11, color: C.textSub }} title="Mirror unreadable — rendering the live store directly until it returns">
                          Last sync · — · {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Hobart' })} · <span style={{ color: C.amber }}>mirror unavailable — showing live store</span> · <span style={{ color: C.text }}>trades_2026.xlsx</span>
                        </span>
                      );
                    })()}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      {periods.map(p => (
                        <button key={p.key} onClick={() => setLedgerPeriod(p.key)}
                          style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6,
                            border: `0.5px solid ${ledgerPeriod === p.key ? C.border : 'transparent'}`,
                            background: ledgerPeriod === p.key ? '#1e1e24' : 'transparent',
                            color: ledgerPeriod === p.key ? C.text : C.textMuted,
                            fontWeight: ledgerPeriod === p.key ? 500 : 400,
                            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    {/* Bearer-only route — a bare <a href> sends no token (401), so the
                        workbook is fetched as a blob through axios and handed to a
                        programmatic link (downloadLedgerXlsx). */}
                    <button onClick={downloadLedgerXlsx} disabled={xlsxBusy}
                      title="Download the nightly-materialised workbook"
                      style={{ fontSize: 10, padding: '3px 12px', borderRadius: 6,
                        border: `0.5px solid ${C.accent}`, background: 'rgba(127,119,221,0.1)',
                        color: C.accent, cursor: xlsxBusy ? 'default' : 'pointer',
                        fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: xlsxBusy ? 0.6 : 1 }}>
                      {xlsxBusy ? 'Preparing…' : 'Open in Excel'}
                    </button>
                  </div>

                  {/* ── Four period cards — same scored math as the Overview ── */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    <StatTile
                      label="Trades"
                      value={totals.trades.toString()}
                      sub={totals.unscored ? `${totals.unscored} unscored` : undefined}
                    />
                    <StatTile
                      label="Net R"
                      value={totals.netR === null ? '—' : fmtRR(totals.netR)}
                      color={totals.netR === null ? C.text : totals.netR >= 0 ? C.green : C.red}
                    />
                    <StatTile
                      label="Win Rate"
                      value={totals.winRate === null ? '—' : `${totals.winRate.toFixed(1)}%`}
                      color={C.accent}
                      sub={totals.scored ? `${totals.wins} of ${totals.scored} scored` : undefined}
                    />
                    <StatTile
                      label="Avg R"
                      value={totals.avgR === null ? '—' : fmtRR(totals.avgR)}
                      color={totals.avgR === null ? C.text : totals.avgR >= 0 ? C.green : C.red}
                    />
                  </div>

                  {/* ── Per-trade log — stacked by date, newest first ── */}
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Trade Log</div>
                    {/* Table — headers and all — is ALWAYS rendered; an empty log speaks
                        through one quiet in-body row, not a swapped view. */}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr>
                            {['Date', 'Time', 'Instr', 'Dir', 'Strategy', 'Grade', 'R', 'Result'].map(h => (
                              <th key={h} style={th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {log.length === 0 && (
                            <tr>
                              <td colSpan={8} style={{ padding: '20px 10px', textAlign: 'center', color: C.textMuted, fontSize: 11 }}>
                                {rows.length === 0
                                  ? (allRows.length === 0 ? 'No trades logged yet' : 'No trades in this period')
                                  : `No trades match “${ledgerQuery.trim()}”`}
                              </td>
                            </tr>
                          )}
                          {groups.map(g => {
                            const dayScored = g.rows.filter(r => r.scored);
                            const dayR = dayScored.reduce((s, r) => s + (r.trade.rr as number), 0);
                            return (
                              <React.Fragment key={`day-${g.key}`}>
                                {/* Day header — the stack boundary */}
                                <tr>
                                  <td colSpan={8} style={{ padding: '12px 10px 5px', borderBottom: `0.5px solid ${C.border}`, background: 'rgba(255,255,255,0.02)', whiteSpace: 'nowrap' }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{fmtDay(g.ts)}</span>
                                    <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 8 }}>
                                      {g.rows.length} trade{g.rows.length === 1 ? '' : 's'}
                                      {dayScored.length > 0 && (
                                        <> · <span style={{ color: dayR >= 0 ? C.green : C.red, fontWeight: 600 }}>{fmtRR(dayR)}</span></>
                                      )}
                                    </span>
                                  </td>
                                </tr>
                                {g.rows.map((r, i) => (
                                  <tr key={r.trade.id || `${g.key}-${i}`}
                                    style={{ borderBottom: `0.5px solid ${C.cardBorder2}`,
                                      background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                                    <td style={{ padding: '8px 10px', color: C.textSub, whiteSpace: 'nowrap' }}
                                      title={r.legacy ? 'Legacy row — no recorded_at; sorted on close/open date' : undefined}>
                                      {fmtDateShort(r.ts)}{r.legacy && <span style={{ color: C.textMuted }}> *</span>}
                                    </td>
                                    <td style={{ padding: '8px 10px', color: C.textSub, whiteSpace: 'nowrap' }}>{fmtTime(r.ts)}</td>
                                    <td style={{ padding: '8px 10px', color: C.text, fontWeight: 500 }}>{r.trade.symbol || '—'}</td>
                                    <td style={{ padding: '8px 10px' }}>
                                      <span style={pill(r.trade.type === 'buy' ? C.greenDark : C.redDark,
                                        r.trade.type === 'buy' ? C.greenLight : C.redLight)}>
                                        {r.trade.type === 'buy' ? 'LONG' : 'SHORT'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px 10px', color: r.trade.strategy ? C.textSub : C.textMuted }}>
                                      {r.trade.strategy || '—'}
                                    </td>
                                    <td style={{ padding: '8px 10px' }}>
                                      {r.grade ? (
                                        <span style={{ color: GRADE_COLOR[r.grade], fontWeight: 600 }}>{r.grade}</span>
                                      ) : (
                                        <span style={{ color: C.textMuted }} title="Unscored — excluded from grades, win rate and average R">—</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '8px 10px', fontWeight: 600,
                                      color: !r.scored ? C.textMuted : (r.trade.rr as number) >= 0 ? C.green : C.red }}>
                                      {r.scored ? fmtRR(r.trade.rr as number) : '—'}
                                    </td>
                                    <td style={{ padding: '8px 10px', fontWeight: 500,
                                      color: r.trade.outcome === 'win' ? C.green
                                        : r.trade.outcome === 'loss' ? C.red : C.textMuted }}>
                                      {r.trade.outcome ? r.trade.outcome.toUpperCase() : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ── "Ask this log" — simple text filter; grounded querying is
                         Arthur-in-conversation, not this line ── */}
                  <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: C.textSub, whiteSpace: 'nowrap' }}>Ask this log</span>
                    <input
                      value={ledgerQuery}
                      onChange={e => setLedgerQuery(e.target.value)}
                      placeholder="Filter — grade, symbol, session, outcome, date…"
                      title="Text filter only — for grounded questions, ask Arthur in chat"
                      style={{
                        flex: '1 1 260px', fontSize: 11, padding: '4px 10px', borderRadius: 6,
                        border: `0.5px solid ${C.border}`, background: '#1a1a1f',
                        color: C.text, fontFamily: 'inherit', outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: 10, color: C.textMuted, whiteSpace: 'nowrap' }}>
                      {log.length} of {rows.length}
                    </span>
                    {ledgerQuery && (
                      <button
                        onClick={() => setLedgerQuery('')}
                        style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6,
                          border: `0.5px solid ${C.border}`, background: 'transparent',
                          color: C.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Clear
                      </button>
                    )}
                  </div>

                  {/* ── Footer ── */}
                  <div style={{ fontSize: 10, color: C.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
                    Read-only mirror of the dashboard store · materialised nightly to trades_2026.xlsx · edits do not flow back.
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ── DREAMS & JOURNALS TAB — read-only psychology surface ───────── */}
        {tab === 'dreams' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(() => {
              // Both boxes are time-threaded. The stamp is the verification: an
              // entry that cannot show one is surfaced as suspect, not smoothed over.
              const fmtStamp = (iso: string | null | undefined) => {
                if (!iso) return null;
                const t = Date.parse(String(iso));
                if (!Number.isFinite(t)) return null;
                return new Date(t).toLocaleString('en-AU', {
                  day: 'numeric', month: 'short', year: '2-digit',
                  hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Hobart',
                });
              };
              const fmtClock = (iso: string | null | undefined) => {
                if (!iso) return null;
                const t = Date.parse(String(iso));
                if (!Number.isFinite(t)) return null;
                return new Date(t).toLocaleTimeString('en-AU', {
                  hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Hobart',
                });
              };

              // THE hallucination check, in one place: a present stamp reads quiet
              // and muted; an absent or unparseable one shouts in amber. Never blank.
              const stampChip = (iso: string | null | undefined, suffix?: string) => {
                const s = fmtStamp(iso);
                return s ? (
                  <span style={{ fontSize: 9, color: C.textMuted, whiteSpace: 'nowrap' }}>
                    {s}{suffix ? ` · ${suffix}` : ''}
                  </span>
                ) : (
                  <span style={{ fontSize: 9, color: C.amber, fontWeight: 600, whiteSpace: 'nowrap' }}
                    title="No timestamp — this entry is not time-threaded. Treat it as unverified.">
                    ⚠ no timestamp
                  </span>
                );
              };

              const boxHeader = (title: string, subtitle: string) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{title}</div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>{subtitle}</div>
                </div>
              );

              const rowText: React.CSSProperties = {
                fontSize: 12, lineHeight: 1.6, color: C.text,
                whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
              };
              // The Rexy row shape: accent rule down the left, stamp above the text.
              const entryRow: React.CSSProperties = {
                borderLeft: `2px solid ${C.border}`, paddingLeft: 10, paddingBottom: 2,
              };

              const nights = dreams ? dreamNights(dreams.insights || []) : [];
              const run = dreams?.last_run || null;
              const jRows = journalRows(data.journal);

              const JOURNAL_TYPE: Record<string, { label: string; color: string; bg: string }> = {
                trade:      { label: 'Trade',        color: C.greenDark, bg: C.greenLight },
                miss:       { label: 'Missed',       color: C.redDark,   bg: C.redLight },
                psychology: { label: 'Psychology',   color: '#3C3489',   bg: '#EEEDFE' },
                summary:    { label: 'Day Summary',  color: C.amber,     bg: C.amberLight },
                weekly:     { label: 'Week Summary', color: '#2a5080',   bg: '#d6eaf8' },
              };

              return (
                <>
                  {/* ══ BOX 1 — DREAM INSIGHTS ══════════════════════════════ */}
                  <div style={card}>
                    {boxHeader(
                      'Dream Insights',
                      `nightly 02:00 · Hobart · owner review${dreams ? ` · ${(dreams.insights || []).length} held` : ''}`,
                    )}

                    {/* Last-run timing strip — when the dream last fired and what
                        window it covered. A failed or empty run is shown as itself,
                        never as a silent gap. */}
                    {run && (
                      <div style={{
                        display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                        paddingBottom: 10, marginBottom: 10,
                        borderBottom: `0.5px solid ${C.cardBorder2}`,
                      }} title={run.run_id || undefined}>
                        <span style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          Last dream
                        </span>
                        <span style={{ fontSize: 11, color: C.text, fontWeight: 500 }}>
                          {fmtStamp(run.completed_at || run.started_at) || (
                            <span style={{ color: C.amber, fontWeight: 600 }}>⚠ never fired</span>
                          )}
                        </span>
                        {run.status && (
                          <span style={{ fontSize: 9, letterSpacing: 1,
                            color: run.status === 'complete' ? C.green : run.status === 'failed' ? C.red : C.textSub }}>
                            {run.status === 'complete' ? '✓' : run.status === 'failed' ? '✗' : '○'} {run.status}
                          </span>
                        )}
                        {(run.window_start || run.window_end) && (
                          <span style={{ fontSize: 9, color: C.textSub }} title="The window of the day this run reasoned over">
                            covered {fmtClock(run.window_start) || 'start'} → {fmtClock(run.window_end) || '?'}
                          </span>
                        )}
                        <span style={{ fontSize: 9, color: C.textMuted, marginLeft: 'auto' }}>
                          {run.insights} insight{run.insights === 1 ? '' : 's'}
                        </span>
                        {run.error && (
                          <span style={{ fontSize: 9, color: C.red, width: '100%' }}>{run.error}</span>
                        )}
                      </div>
                    )}

                    {/* A corrupt read is a VISIBLE failure — never mistaken for a quiet night. */}
                    {dreams?.degraded && (
                      <div style={{ fontSize: 10, color: C.red, marginBottom: 8 }}>
                        dream store degraded — a lane is unreadable; what shows below may be partial
                      </div>
                    )}

                    {dreamsErr ? (
                      <div style={{ fontSize: 11, color: C.textMuted, padding: '4px 0' }}>
                        not available — {dreamsErr}
                      </div>
                    ) : dreamsLoading && !dreams ? (
                      <div style={{ fontSize: 11, color: C.textMuted, padding: '4px 0' }}>loading…</div>
                    ) : dreams?.insights_status === 'unreadable' ? (
                      <div style={{ fontSize: 11, color: C.red, padding: '4px 0' }}>
                        insight store unreadable — visible failure
                      </div>
                    ) : nights.length === 0 ? (
                      /* Honest pre-first-run state — 'fresh' is a quiet night, not a break. */
                      <div style={{ fontSize: 11, color: C.textMuted, padding: '4px 0' }}>
                        no dream yet · next dream 02:00
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {nights.map(n => {
                          const isLastRunNight = !!(run && run.night && run.night === n.night);
                          return (
                            <div key={n.night} style={{ borderTop: `0.5px solid ${C.cardBorder2}`, padding: '8px 2px 2px' }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                <span style={{ fontSize: 11, color: C.text, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                  {n.night}
                                </span>
                                {isLastRunNight && run?.status && (
                                  <span style={{ fontSize: 9, letterSpacing: 1,
                                    color: run.status === 'complete' ? C.green : run.status === 'failed' ? C.red : C.textSub }}>
                                    {run.status === 'complete' ? '✓ ran' : run.status === 'failed' ? '✗ failed' : `○ ${run.status}`}
                                  </span>
                                )}
                                <span style={{ fontSize: 9, color: C.textMuted, marginLeft: 'auto' }}>
                                  {n.rows.length} insight{n.rows.length === 1 ? '' : 's'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                                {n.rows.map(ins => (
                                  <div key={ins.id} style={entryRow} title={ins.id}>
                                    <div style={{ marginBottom: 3 }}>{stampChip(ins.created_at)}</div>
                                    {/* Arthur's words, VERBATIM — this tab mirrors, never reshapes. */}
                                    <div style={rowText}>{ins.content}</div>
                                    <ArthurDreamReview insight={ins} onReview={(action, reason) => reviewDream(ins, action, reason)} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ══ BOX 2 — JOURNALS ════════════════════════════════════ */}
                  <div style={card}>
                    {boxHeader(
                      'Journals',
                      `sorted on the write stamp · newest first · Hobart · ${jRows.length} entr${jRows.length === 1 ? 'y' : 'ies'}`,
                    )}

                    {jRows.length === 0 ? (
                      <div style={{ fontSize: 11, color: C.textMuted, padding: '4px 0' }}>
                        no journal entries yet
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {jRows.map(r => {
                          const ts = JOURNAL_TYPE[r.entry.type] || JOURNAL_TYPE.trade;
                          return (
                            <div key={r.entry.id} style={{ ...entryRow, borderTop: `0.5px solid ${C.cardBorder2}`, paddingTop: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                                <span style={pill(ts.color, ts.bg)}>{ts.label}</span>
                                {/* The stamp — first-class on every row. `date` fallback is
                                    labelled so a legacy row can't pass as spine-stamped. */}
                                {stampChip(r.entry.recorded_at || r.entry.date,
                                  r.spine === 'recorded_at' ? undefined : 'entry date')}
                                {r.entry.tradeId && (
                                  <span style={{ fontSize: 9, color: C.textSub, whiteSpace: 'nowrap' }}
                                    title={`Linked trade ${r.entry.tradeId}`}>
                                    ↳ trade {r.entry.tradeId}
                                  </span>
                                )}
                              </div>
                              <div style={rowText}>{r.entry.content}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ── Footer ── */}
                  <div style={{ fontSize: 10, color: C.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
                    Dreams are written by the 02:00 pass. Confirm or reject individual points above. Journals remain read-only here.
                    <br />
                    Every entry is time-threaded — an entry without a timestamp did not happen on the record.
                  </div>
                </>
              );
            })()}
          </div>
        )}


        {/* ── DEEP EXPERTISE TAB — additive doctrine tracker ─────────────── */}
        {tab === 'expertise' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Doctrine Status</div>
              {doctrineLoading && !doctrine ? (
                <EmptyState label="Loading doctrine status" />
              ) : doctrineErr ? (
                <EmptyState label={`Doctrine status unavailable — ${doctrineErr}`} />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                  <StatTile label="Strategy Routing" value={doctrine?.strategy_routing_status === 'AVAILABLE' ? 'Available' : 'Unavailable'} color={doctrine?.strategy_routing_status === 'AVAILABLE' ? C.green : C.textMuted} />
                  <StatTile label="Strategy Doctrine" value={strategyDoctrineAvailable ? 'Installed' : 'Not installed'} color={doctrine?.strategy_substantive_status === 'OWNER_DOCTRINE_AVAILABLE' ? C.green : C.amber} sub={strategyDoctrineAvailable && doctrine?.strategy_version ? `Version ${doctrine.strategy_version}` : undefined} />
                  <StatTile label="Psychology Routing" value={doctrine?.psychology_routing_status === 'AVAILABLE' ? 'Available' : 'Unavailable'} color={doctrine?.psychology_routing_status === 'AVAILABLE' ? C.green : C.textMuted} />
                  <StatTile label="Psychology Doctrine" value={doctrine?.psychology_substantive_status === 'OWNER_DOCTRINE_AVAILABLE' ? 'Installed' : 'Not installed'} color={doctrine?.psychology_substantive_status === 'OWNER_DOCTRINE_AVAILABLE' ? C.green : C.amber} sub={doctrine?.psychology_leading_document || undefined} />
                </div>
              )}
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
              Read-only doctrine tracker · Strategy routing and Psychology routing are separate from substantive owner doctrine.
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  );
}
